import { useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import toast from "react-hot-toast";
import RefundBreakdownPanel from "./RefundBreakdownPanel";
import CustomerRequestCard from "./CustomerRequestCard";
import { StatusChip } from "./NextActionCard";

// ─── Step logic ──────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: "Airline fee" },
  { id: 2, label: "Check refund" },
  { id: 3, label: "Send to customer" },
  { id: 4, label: "Cancel & refund" },
];

// Statuses reached only after the booking was cancelled with the airline.
const POST_AIRLINE_CANCEL_STATUSES = ["cancelled_at_airline", "refund_processed", "cancellation_processed"];

// The quote is with the customer. Their Accept in the app auto-cancels with TBO
// and refunds, so the panel polls to pick that up instead of offering a second
// cancel that the backend rejects as "already cancelled".
const AWAITING_CUSTOMER_STATUSES = ["charges_notified", "customer_accepted"];
const AWAITING_CUSTOMER_POLL_MS = 15000;

// Statuses where a charge is saved and the cancellation is still pending, so the
// fee quote (with Accept / Decline) can be re-sent to the customer.
const FEE_RENOTIFY_STATUSES = ["charges_calculated", "charges_quoted", "charges_notified"];

// Step 3 option for a DGCA free-look ticket: sends the full refund (₹0 charge)
// for the customer to Accept / Reject, recording the TBO confirmation first.
const DGCA_REFUND_NOTIFY = "DGCA_REFUND_CONFIRMATION";

// Backend may serialize the TINYINT as 1, "1" or true; normalize once so every
// DGCA gate in the panel agrees with the backend's Number(...) === 1 check.
export function isDgcaEligibleTicket(ticket) {
  const v = ticket?.is_dgca_eligible;
  return v === true || Number(v) === 1;
}

export function deriveActiveStep(cancellationStatus, hasLocalCharges, hasSavedCharge) {
  const s = cancellationStatus || "";
  if (POST_AIRLINE_CANCEL_STATUSES.includes(s)) return 5;
  if (["customer_accepted", "charges_notified"].includes(s)) return 4;
  // "charges_quoted" alone isn't enough — the backend sets this same status for
  // a partial-cancellation fare-rule suggestion fetch too (never an actual saved
  // charge), so on a reload it must NOT be treated as done unless a charge was
  // genuinely saved (ticket.cancellation_charge, only ever populated by
  // updateCancellationCharge). hasLocalCharges (this session's own fetch result)
  // is a separate, already-correct signal and doesn't need this same guard.
  if ((s === "charges_quoted" && hasSavedCharge) || hasLocalCharges) return 2;
  return 1;
}

// Step 3 (Notify Customer) is not strictly sequential: the charge save itself
// already notifies the customer, and the post-cancellation messages only make
// sense AFTER Step 4. So Step 3 stays open (never locked or collapsed) once a
// charge is saved and after the airline cancel, instead of depending on
// activeStep, which never lands on 3.
export function isNotifyStepAvailable(cancellationStatus, hasSavedCharge) {
  const s = cancellationStatus || "";
  if (POST_AIRLINE_CANCEL_STATUSES.includes(s)) return true;
  return !!hasSavedCharge && [...FEE_RENOTIFY_STATUSES, "customer_accepted"].includes(s);
}

// Which messages the backend will accept for the current status
// (notifyTicketCustomer): post-cancellation messages only after the airline
// cancel; before it, only a re-send of the fee quote.
export function notifyOptionsForStatus(cancellationStatus) {
  const s = cancellationStatus || "";
  if (POST_AIRLINE_CANCEL_STATUSES.includes(s)) {
    return [
      { value: "TICKET_CANCELLED_REFUND_PROCESSING", label: "Booking cancelled — refund is being processed" },
      { value: "REFUND_ISSUED", label: "Refund has been issued to their account" },
      { value: "TICKET_CANCELLED", label: "Booking has been cancelled (no refund message)" },
    ];
  }
  if (FEE_RENOTIFY_STATUSES.includes(s)) {
    return [{ value: "CANCELLATION_FEE_QUOTED", label: "Re-send cancellation fee (customer can Accept / Decline)" }];
  }
  return [];
}

// Classifies a failed getTicketCancellationCharges call into the specific
// reason it failed, so the agent sees more than a generic "failed" message.
// The backend's error shapes (tickets.admin.controller.js, unchanged by this
// fix) don't share one "code" field across every failure branch, so this
// distinguishes them by which fields are present on the error response:
//   - tbo_status present  -> TBO responded but with a non-success status
//   - provider_error present -> transport/communication failure reaching TBO
//   - neither, still a response -> auth/token failure (the remaining 502 case)
//   - no response at all, axios timeout -> client-side timeout
//   - no response at all, anything else -> network/connectivity error
function classifyChargesFetchError(err) {
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
    return {
      kind: "timeout",
      title: "Request timed out",
      detail: "Request to fetch airline charges timed out — please try again.",
    };
  }
  const data = err?.response?.data;
  if (!err?.response) {
    return {
      kind: "network",
      title: "Network error",
      detail: err?.message || "Could not reach the server — check your connection and try again.",
    };
  }
  if (data?.tbo_status !== undefined) {
    return {
      kind: "tbo_status",
      title: "Airline system error",
      detail: data?.message || `The airline system returned an error (status ${data.tbo_status}).`,
    };
  }
  if (data?.provider_error !== undefined) {
    return {
      kind: "transport",
      title: "Could not reach the airline system",
      detail: data?.message || "A communication error occurred while contacting the airline system.",
    };
  }
  return {
    kind: "auth_or_other",
    title: "Airline system authentication failed",
    detail: data?.message || err?.message || "Failed to fetch charges from the airline system.",
  };
}

// Extracts a numeric fare from a passenger's `fare` JSON — mirrors the
// backend's sumPassengerFares extraction (modules/cancellation/cancellation.service.js)
// so the per-passenger exceeds-fare hint below matches what the server will
// actually check. Not required to be byte-identical — the backend remains
// authoritative; this is a client-side sanity hint only.
function getPassengerFareAmount(passenger) {
  const raw = passenger?.fare;
  if (raw == null) return 0;
  if (typeof raw === "number") return raw;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    // Same priority as the backend: rows are written snake_case (offered_fare).
    const primary = Number(
      raw.offered_fare ?? raw.OfferedFare ?? raw.published_fare ?? raw.PublishedFare ?? raw.TotalFare ?? NaN
    );
    if (Number.isFinite(primary) && primary > 0) return primary;
    return Object.values(raw).reduce((sum, v) => {
      const n = Number(v);
      return sum + (Number.isFinite(n) && n > 0 ? n : 0);
    }, 0);
  }
  return 0;
}

// Customer-facing lines of a refund-breakdown preview (agent_quote from
// /admin/tickets/refund-breakdown, i.e. the server refund engine the payout
// uses), as "₹4,998 total fare − ₹300 charge − … = ₹4,255".
export function refundFormulaText(q) {
  if (!q) return "";
  const fmt = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  const parts = [
    ["total fare", q.totalFarePaid, true],
    ["charge", q.cancellationCharge],
    ["conv fee", q.convenienceFee],
    ["airline service fee", q.airlineServiceFee],
    ["promo", q.promoCodeAmountDeducted],
  ].filter(([, v, first]) => first || Number(v) > 0);
  return `${parts.map(([label, v], i) => `${i ? " − " : ""}${fmt(v)} ${label}`).join("")} = ${fmt(q.refundAmount)}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StepTracker({ activeStep, isDgca = false, wholeTrip = false }) {
  const done = activeStep >= 5;
  // A free (DGCA) cancellation has no airline fee: step 1 is the TBO check.
  // On a whole trip, steps 3 and 4 are shared by both flights (below).
  const steps = STEPS.map((x) => {
    if (x.id === 1 && isDgca) return { ...x, label: "Check with TBO" };
    if (x.id === 3 && wholeTrip) return { ...x, label: "Send (both flights)" };
    if (x.id === 4 && wholeTrip) return { ...x, label: "Cancel & refund (automatic)" };
    return x;
  });
  return (
    <div className="flex items-start w-full mb-5">
      {steps.map((step, i) => {
        const complete = done || step.id < activeStep;
        const active = step.id === activeStep && !done;
        const isLast = i === STEPS.length - 1;
        return (
          <div key={step.id} className="flex-1 flex flex-col items-center relative">
            {i > 0 && (
              <div
                className={`absolute top-3.5 h-0.5 transition-colors ${complete || active ? "bg-green-400" : "bg-gray-200"}`}
                style={{ left: 0, right: "50%", transform: "translateX(-50%)", width: "100%" }}
              />
            )}
            {!isLast && (
              <div
                className={`absolute top-3.5 h-0.5 transition-colors ${complete ? "bg-green-400" : "bg-gray-200"}`}
                style={{ left: "50%", width: "50%" }}
              />
            )}
            <div
              className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                complete
                  ? "bg-green-500 text-white"
                  : active
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-400 border border-gray-300"
              }`}
            >
              {complete ? "✓" : step.id}
            </div>
            <span
              className={`mt-1.5 text-[10px] font-semibold text-center leading-tight whitespace-nowrap ${
                active ? "text-blue-700" : complete ? "text-green-600" : "text-gray-400"
              }`}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DoneBanner({ cancellationStatus }) {
  // cancellation_processed / refund_processed: the refund has been paid.
  // cancelled_at_airline: cancelled, refund still to be paid.
  const label =
    cancellationStatus === "cancelled_at_airline"
      ? "Cancelled with the airline. The refund is still being processed."
      : "Cancelled and refunded.";
  return (
    <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800 font-semibold flex items-center gap-2">
      <span className="text-base">✅</span>
      <span>{label}</span>
    </div>
  );
}

function RefundSummaryCard({ refundSummary, formatINR }) {
  if (!refundSummary) return null;

  const {
    total_fare_at_booking,
    airline_cancellation_charge,
    convenience_fee_forfeited,
    cashback_clawed_back,
    cash_refunded_to_bank,
    giftcard_refunded,
    credit_line_refunded,
    total_refunded,
    giftcard_credit,
    razorpay_refund_id,
    arn,
    rrn,
    razorpay_status,
    cancel_type,
    refund_type,
    is_fully_processed,
    cancelled_passenger_ids,
    processed_at,
  } = refundSummary;

  const hasCash       = Number(cash_refunded_to_bank) > 0;
  const hasGiftcard   = Number(giftcard_refunded) > 0;
  const hasCreditLine = Number(credit_line_refunded) > 0;
  const hasArn        = arn && String(arn).trim();
  const hasRrn        = rrn && String(rrn).trim();
  const paxCount      = Array.isArray(cancelled_passenger_ids) ? cancelled_passenger_ids.length : null;
  const onlyGiftcard   = hasGiftcard && !hasCash && !hasCreditLine;
  const onlyCreditLine = hasCreditLine && !hasCash && !hasGiftcard;

  const statusColor =
    razorpay_status === "processed"
      ? "text-green-700 bg-green-50 border-green-200"
      : razorpay_status === "pending"
      ? "text-yellow-700 bg-yellow-50 border-yellow-200"
      : razorpay_status === "failed"
      ? "text-red-700 bg-red-50 border-red-200"
      : "text-gray-600 bg-gray-50 border-gray-200";

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="bg-gray-900 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-base">💸</span>
          <span className="text-sm font-bold text-white">Refund Summary</span>
        </div>
        <div className="flex items-center gap-2">
          {cancel_type && (
            <span className="text-[10px] font-semibold bg-gray-700 text-gray-200 px-2 py-0.5 rounded-full capitalize">
              {cancel_type === "partial" ? `Partial${paxCount ? ` (${paxCount} pax)` : ""}` : "Full"}
            </span>
          )}
          {refund_type === "DGCA_REFUND" && (
            <span className="text-[10px] font-semibold bg-amber-500 text-white px-2 py-0.5 rounded-full">
              DGCA
            </span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        {/* Deduction breakdown */}
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-3 space-y-1.5">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
            Deductions from Booking
          </div>

          {total_fare_at_booking != null && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total fare paid</span>
              <span className="font-semibold text-gray-900 tabular-nums">
                {formatINR(total_fare_at_booking)}
              </span>
            </div>
          )}

          {Number(airline_cancellation_charge) > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Airline cancellation charge</span>
              <span className="font-semibold text-red-600 tabular-nums">
                − {formatINR(airline_cancellation_charge)}
              </span>
            </div>
          )}

          {Number(convenience_fee_forfeited) > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 flex items-center gap-1">
                Convenience fee
                <span className="text-[9px] font-semibold bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full">
                  non-refundable
                </span>
              </span>
              <span className="font-semibold text-red-600 tabular-nums">
                − {formatINR(convenience_fee_forfeited)}
              </span>
            </div>
          )}

          {Number(cashback_clawed_back) > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Cashback reversed</span>
              <span className="font-semibold text-orange-600 tabular-nums">
                − {formatINR(cashback_clawed_back)}
              </span>
            </div>
          )}
        </div>

        {/* Refund destinations */}
        <div className={`rounded-xl border px-3 py-3 space-y-2 ${onlyGiftcard ? "bg-purple-50/40 border-purple-100" : onlyCreditLine ? "bg-indigo-50/40 border-indigo-100" : "bg-blue-50/40 border-blue-100"}`}>
          <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${onlyGiftcard ? "text-purple-500" : onlyCreditLine ? "text-indigo-500" : "text-blue-500"}`}>
            Refund Credited To
          </div>

          {/* Gift-card-only booking banner */}
          {onlyGiftcard && (
            <div className="rounded-lg bg-purple-100 border border-purple-200 px-2.5 py-2 text-xs text-purple-900 flex items-start gap-2">
              <span className="text-sm mt-0.5">🎁</span>
              <div>
                <span className="font-semibold">Booking paid via Gift Card.</span>
                <span className="ml-1">
                  The eligible refund of{" "}
                  <span className="font-bold">{formatINR(giftcard_refunded)}</span> has been
                  credited to the customer&apos;s in-app wallet — no cash goes to a bank account.
                </span>
              </div>
            </div>
          )}

          {/* Credit-line-only booking banner */}
          {onlyCreditLine && (
            <div className="rounded-lg bg-indigo-100 border border-indigo-200 px-2.5 py-2 text-xs text-indigo-900 flex items-start gap-2">
              <span className="text-sm mt-0.5">💳</span>
              <div>
                <span className="font-semibold">Booking paid via Credit Line.</span>
                <span className="ml-1">
                  The eligible refund of{" "}
                  <span className="font-bold">{formatINR(credit_line_refunded)}</span> has been
                  adjusted against the customer&apos;s credit-line invoice — no cash goes to a bank account.
                </span>
              </div>
            </div>
          )}

          {hasCash && (
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-base">🏦</span>
                <span className="text-sm text-gray-700">
                  {hasGiftcard || hasCreditLine ? "Bank / UPI (remaining)" : "Original payment method"}
                </span>
              </div>
              <span className="text-sm font-bold text-gray-900 tabular-nums">
                {formatINR(cash_refunded_to_bank)}
              </span>
            </div>
          )}

          {hasGiftcard && (hasCash || hasCreditLine) && (
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-base">🎁</span>
                <span className="text-sm text-gray-700">Gift card → in-app wallet</span>
              </div>
              <span className="text-sm font-bold text-purple-700 tabular-nums">
                {formatINR(giftcard_refunded)}
              </span>
            </div>
          )}

          {hasCreditLine && (hasCash || hasGiftcard) && (
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-base">💳</span>
                <span className="text-sm text-gray-700">Credit line adjustment</span>
              </div>
              <span className="text-sm font-bold text-indigo-700 tabular-nums">
                {formatINR(credit_line_refunded)}
              </span>
            </div>
          )}

          {!hasCash && !hasGiftcard && !hasCreditLine && (
            <div className="text-xs text-gray-400 italic flex items-center gap-1.5">
              <span>ℹ️</span>
              <span>
                No cash, gift card, or credit line refund was processed — the booking may have
                been covered entirely by NavCash or promo discount, or the refund is still pending.
              </span>
            </div>
          )}

          <div className={`border-t pt-2 flex justify-between items-center ${onlyGiftcard ? "border-purple-200" : onlyCreditLine ? "border-indigo-200" : "border-blue-200"}`}>
            <span className="text-sm font-bold text-gray-800">Total refunded</span>
            <span className={`text-base font-bold tabular-nums ${onlyGiftcard ? "text-purple-700" : onlyCreditLine ? "text-indigo-700" : "text-blue-700"}`}>
              {formatINR(total_refunded)}
            </span>
          </div>
        </div>

        {/* Gift card expiry extension */}
        {giftcard_credit?.expiry_extended && (
          <div className="rounded-xl border border-purple-200 bg-purple-50 px-3 py-2.5 space-y-1">
            <div className="text-xs font-semibold text-purple-800">
              🎁 Gift card validity extended
            </div>
            <div className="flex justify-between text-xs text-purple-700">
              <span>Previous expiry</span>
              <span className="line-through">
                {giftcard_credit.previous_expiry
                  ? new Date(giftcard_credit.previous_expiry).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })
                  : "—"}
              </span>
            </div>
            <div className="flex justify-between text-xs text-purple-700 font-semibold">
              <span>New expiry</span>
              <span>
                {giftcard_credit.new_expiry
                  ? new Date(giftcard_credit.new_expiry).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })
                  : "—"}
              </span>
            </div>
          </div>
        )}

        {/* Payment gateway details */}
        {(razorpay_refund_id || hasArn || hasRrn || razorpay_status) && (
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-3 space-y-1.5">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
              Payment Gateway
            </div>

            {razorpay_status && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Razorpay status</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize ${statusColor}`}>
                  {razorpay_status}
                </span>
              </div>
            )}

            {razorpay_refund_id && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Razorpay refund ID</span>
                <span className="text-xs font-mono text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                  {razorpay_refund_id}
                </span>
              </div>
            )}

            {hasArn && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">ARN</span>
                <span className="text-xs font-mono text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                  {arn}
                </span>
              </div>
            )}

            {hasRrn && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">RRN</span>
                <span className="text-xs font-mono text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                  {rrn}
                </span>
              </div>
            )}

            {processed_at && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Processed at</span>
                <span className="text-xs text-gray-700">
                  {new Date(processed_at).toLocaleString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Recovery warning */}
        {razorpay_status && razorpay_status !== "processed" && hasCash && (
          <div className="rounded-xl border border-yellow-300 bg-yellow-50 px-3 py-2.5 text-xs text-yellow-800 flex items-start gap-2">
            <span className="text-sm">⚠️</span>
            <span>
              Cash refund status is <strong>{razorpay_status}</strong>.
              {razorpay_status === "failed"
                ? " Use the Retry Failed Refund button to re-attempt."
                : " The refund is still being processed by the payment gateway."}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function FareSummary({ booking, formatINR, tripFare = null, showTripTotal = true }) {
  if (!booking) return null;
  const thisLeg = tripFare?.legs?.find((l) => l.is_this_ticket) ?? null;
  // Whole trip (two flights): one fare box for the trip, shown once (Flight 1).
  if (thisLeg && tripFare.legs.length > 1) {
    return showTripTotal ? <TripFareBreakdown tripFare={tripFare} formatINR={formatINR} /> : null;
  }
  const baseFare = Number(booking.base_fare) || 0;
  const airlineTax = Number(booking.airline_tax) || 0;
  const grossFare = baseFare + airlineTax;
  const totalFare = Number(booking.totalfare) || 0;
  const displayFare = grossFare > 0 ? grossFare : totalFare;
  const convFee = Number(booking.convenience_fee) || 0;
  const promo =
    Number(booking.applied_promo_code) === 1
      ? Number(booking.applied_promo_code_amount) || 0
      : 0;
  const navcash =
    Number(booking.is_wallet_applied) === 1
      ? Number(booking.wallet_points) || 0
      : 0;
  const giftcard =
    booking.is_giftcard_applied === true || Number(booking.is_giftcard_applied) === 1
      ? Number(booking.applied_giftcard_amount) || 0
      : 0;
  const creditLine =
    booking.is_credit_line_applied === true || Number(booking.is_credit_line_applied) === 1
      ? Number(booking.applied_credit_line_amount) || 0
      : 0;
  // totalfare also holds other charges / round-off (ticket 659: ₹148), which
  // base + taxes leaves out. Shown as its own line so the summary adds up to
  // what the customer paid and to the fare the refund is worked out from.
  const otherCharges =
    grossFare > 0 && totalFare > 0 ? Math.max(0, Math.round((totalFare - grossFare - convFee) * 100) / 100) : 0;
  const amountPaid = Math.max(0, displayFare + otherCharges + convFee - promo - navcash - giftcard - creditLine);

  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/40 px-4 py-3 mb-4">
      <div className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-2">
        {thisLeg ? `Fare for this flight${thisLeg.route ? ` (${thisLeg.route})` : ""}` : "Fare Summary"}
      </div>
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-sm text-gray-700">Base Fare + Taxes</span>
        <span className="text-sm font-semibold tabular-nums">{formatINR(displayFare)}</span>
      </div>
      {otherCharges > 0 && (
        <div className="flex justify-between items-baseline mb-1">
          <span className="text-sm text-gray-500">Other Charges</span>
          <span className="text-sm tabular-nums">+ {formatINR(otherCharges)}</span>
        </div>
      )}
      {promo > 0 && (
        <div className="flex justify-between items-baseline mb-1">
          <span className="text-sm text-gray-500">
            Promo Discount
            {booking.promo_code && (
              <span className="ml-1 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">
                {booking.promo_code}
              </span>
            )}
          </span>
          <span className="text-sm text-green-600 tabular-nums">− {formatINR(promo)}</span>
        </div>
      )}
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-sm text-gray-500">
          Convenience Fee
          <span className="ml-1 text-[10px] text-orange-600 font-semibold bg-orange-50 px-1.5 py-0.5 rounded-full">
            non-refundable
          </span>
        </span>
        <span className="text-sm tabular-nums">+ {formatINR(convFee)}</span>
      </div>
      {navcash > 0 && (
        <div className="flex justify-between items-baseline mb-1">
          <span className="text-sm text-gray-500">NavCash Used</span>
          <span className="text-sm text-blue-600 tabular-nums">− {formatINR(navcash)}</span>
        </div>
      )}
      {giftcard > 0 && (
        <div className="flex justify-between items-baseline mb-1">
          <span className="text-sm text-purple-700">
            Gift Card Applied
            {(booking.is_giftcard_fully_claimed === true || Number(booking.is_giftcard_fully_claimed) === 1) && (
              <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold">
                Fully Claimed
              </span>
            )}
          </span>
          <span className="text-sm text-purple-700 tabular-nums">− {formatINR(giftcard)}</span>
        </div>
      )}
      {creditLine > 0 && (
        <div className="flex justify-between items-baseline mb-1">
          <span className="text-sm text-indigo-700">Credit Line Applied</span>
          <span className="text-sm text-indigo-700 tabular-nums">− {formatINR(creditLine)}</span>
        </div>
      )}
      {giftcard > 0 ? (
        <div className="border-t border-gray-200 mt-2 pt-2 space-y-1.5">
          <div className="flex justify-between items-baseline">
            <span className="text-sm font-bold text-gray-900">Cash Paid</span>
            <span className={`text-base font-bold tabular-nums ${amountPaid === 0 ? "text-gray-400" : "text-gray-900"}`}>
              {formatINR(amountPaid)}
            </span>
          </div>
          <div className="rounded-lg bg-purple-50 border border-purple-100 px-2.5 py-2 text-xs text-purple-800">
            {amountPaid === 0 ? (
              <>
                <span className="font-semibold">Booking paid entirely via Gift Card.</span>
                {" "}On cancellation, the eligible refund will be credited to the customer&apos;s in-app wallet — not to a bank account.
              </>
            ) : (
              <>
                <span className="font-semibold">Gift Card portion ({formatINR(giftcard)}) will be refunded to the customer&apos;s wallet.</span>
                {" "}Only the remaining {formatINR(amountPaid)} goes back to the customer&apos;s bank account.
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between items-baseline">
          <span className="text-sm font-bold text-gray-900">Amount Paid</span>
          <span className="text-base font-bold text-gray-900 tabular-nums">{formatINR(amountPaid)}</span>
        </div>
      )}

    </div>
  );
}

// Split round trip: each flight is its own booking and its own ticket, so the
// summary above is one flight. This shows what the customer paid for the whole
// trip (ticket #671: ₹14,275 for 2 passengers, with each ticket showing half).
function TripFareBreakdown({ tripFare, formatINR }) {
  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/40 px-4 py-3 mb-4 space-y-1" data-testid="trip-fare">
      <div className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-1">
        Fare for the whole trip ({tripFare.legs.length} flights)
      </div>
      {tripFare.legs.map((l, i) => (
        <div key={l.booking_id} className="flex justify-between items-baseline text-sm">
          <span className="text-gray-700">
            Flight {i + 1}: {l.route || `Booking ${l.booking_id}`}
          </span>
          <span className="tabular-nums font-semibold">{formatINR(l.total_fare)}</span>
        </div>
      ))}
      <div className="flex justify-between items-baseline text-sm text-gray-500">
        <span>
          Our booking fee{" "}
          <span className="text-[10px] font-semibold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">not refundable</span>
          <span className="ml-1 text-xs">(already included above)</span>
        </span>
        <span className="tabular-nums">{formatINR(tripFare.total_convenience_fee)}</span>
      </div>
      <div className="flex justify-between items-baseline border-t border-blue-100 pt-2 mt-1">
        <span className="text-sm font-bold text-gray-900">Total paid for the trip</span>
        <span className="text-base font-bold text-gray-900 tabular-nums">{formatINR(tripFare.total_fare)}</span>
      </div>
    </div>
  );
}

// Per TBO's documented DGCA look-in process: shown for every DGCA-eligible
// ticket, full or partial alike. Two distinct states:
//   - Not yet confirmed: blocks processing. The agent must record what TBO
//     told them (and ideally a reference) before the cancellation can be
//     executed at all — see the tbo_dgca_confirmed gate in
//     executeCancellation.service.js / processTicketCancellation.
//   - Confirmed: shows who confirmed and when, and clears the way to Step 4.
// The customer never sees a charge breakdown for a DGCA case — this entire
// card is agent-facing only, inside the admin panel.
function DgcaBanner({
  ticket,
  booking,
  formatINR,
  tboDgcaNote,
  setTboDgcaNote,
  tboDgcaReference,
  setTboDgcaReference,
  tboDgcaSubmitting,
  onConfirmTboDgca,
}) {
  if (!ticket || !isDgcaEligibleTicket(ticket)) return null;
  const convFee = booking ? Number(booking.convenience_fee) || 0 : 0;
  const maxRefund = dgcaMaxRefund(booking);
  const confirmed = Number(ticket.tbo_dgca_confirmed) === 1;

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 mb-4 space-y-2">
      <div className="text-sm font-bold text-amber-900">Free cancellation (DGCA 48-hour rule)</div>
      <p className="text-sm text-amber-900">
        The customer cancelled within 48 hours of booking, so the airline charges nothing. The
        customer gets <span className="font-bold">{formatINR(maxRefund)}</span> back: the fare
        minus our booking fee of {formatINR(convFee)}.
      </p>
      <p className="text-xs text-amber-800">
        If the airline itself cancelled the flight, our booking fee is refunded too: choose
        &ldquo;Flight Cancelled by Airline&rdquo; when you cancel.
      </p>
      {ticket.dgca_check_reason && (
        <details className="text-xs text-amber-700">
          <summary className="cursor-pointer select-none">Why this is a free cancellation</summary>
          <p className="mt-1">{ticket.dgca_check_reason}</p>
        </details>
      )}

      {confirmed ? (
        <div className="rounded-lg bg-green-50 border border-green-300 px-3 py-2.5 text-xs text-green-800 space-y-0.5">
          <p className="font-bold">✅ TBO confirmed the full refund</p>
          <p>
            Confirmed by {ticket.tbo_dgca_confirmed_by_name || "an agent"}
            {ticket.tbo_dgca_confirmed_at
              ? ` on ${new Date(ticket.tbo_dgca_confirmed_at).toLocaleString("en-IN")}`
              : ""}
            .
          </p>
          {ticket.tbo_dgca_confirmation_note && (
            <p className="italic text-green-700">&ldquo;{ticket.tbo_dgca_confirmation_note}&rdquo;</p>
          )}
          {["dgca_hold", "pending_charges", "charges_quoted", "charges_calculated", ""].includes(ticket.cancellation_status || "") && (
            <p>Next: Step 3, send the refund to the customer.</p>
          )}
        </div>
      ) : (
        <p className="text-xs font-semibold text-red-800">
          Not checked with TBO yet. Start with Step 1 below: Check with TBO.
        </p>
      )}
    </div>
  );
}

// Step 1 for a DGCA free-look ticket: what the agent does instead of fetching
// or typing an airline fee.
export function DgcaNoFeeSteps({ tboConfirmed, booking, formatINR, tripFare = null, confirmForm = null, wholeTrip = false }) {
  const convFee = Number(booking?.convenience_fee) || 0;
  return (
    <div className="space-y-2 text-sm text-gray-700" data-testid="dgca-no-fee-steps">
      {/* The TBO check is this step's job, so its form lives here. */}
      {!tboConfirmed && confirmForm}
      <p className="text-sm">
        Free cancellation: there is no airline fee to get. The customer gets{" "}
        <span className="font-semibold">{formatINR(dgcaMaxRefund(booking))}</span> back
        {tripFare ? " for this flight" : ""} (our booking fee of {formatINR(convFee)} is kept).
      </p>
      <ol className="list-decimal pl-5 space-y-1">
        <li className={tboConfirmed ? "text-green-700" : ""}>
          {tboConfirmed ? "✅ TBO confirmed the full refund." : "Ask TBO to confirm the full refund, then fill in the form above and click Confirm TBO Refund."}
        </li>
        {wholeTrip ? (
          <>
            <li>
              When both flights are checked, send them together in{" "}
              <span className="font-semibold">Step 3: Send to customer (both flights)</span> below.
            </li>
            <li>When the customer accepts, both flights are cancelled and refunded automatically.</li>
          </>
        ) : (
          <>
            <li>
              Then go to <span className="font-semibold">Step 3</span> and send the customer the refund
              (&ldquo;Refund for confirmation, no cancellation fee&rdquo;).
            </li>
            <li>When the customer accepts, the cancellation and refund happen automatically (Step 4).</li>
          </>
        )}
      </ol>
      <p className="text-xs text-amber-700">
        If TBO says no, do not send anything to the customer. Escalate the ticket.
      </p>
    </div>
  );
}

// The "Confirm TBO Refund" form. Rendered in the DGCA banner and again inside
// Step 4, where the agent hits the lock, so it never has to be found above.
// Both copies share the same state, so typing in one shows in the other.
export function DgcaConfirmForm({
  tboDgcaNote,
  setTboDgcaNote,
  tboDgcaReference,
  setTboDgcaReference,
  tboDgcaSubmitting,
  onConfirmTboDgca,
}) {
  return (
    <div className="rounded-lg bg-red-50 border border-red-300 px-3 py-3 space-y-2">
      <p className="text-xs font-bold text-red-800">Check with TBO first</p>
      <p className="text-xs text-red-700">
        Call or email TBO and ask them to confirm the airline will refund the full fare. Then
        write down what they said and their reference number. This flight cannot be cancelled
        until you do.
      </p>
      <div>
        <label className="text-xs text-red-700 mb-1 block font-medium">
          What did TBO say? <span className="font-normal">(required)</span>
        </label>
        <textarea
          value={tboDgcaNote}
          onChange={(e) => setTboDgcaNote(e.target.value)}
          rows={2}
          placeholder="e.g. TBO confirmed the airline will refund the full fare for this PNR"
          disabled={tboDgcaSubmitting}
          className="w-full border border-red-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-60"
        />
      </div>
      <div>
        <label className="text-xs text-red-700 mb-1 block font-medium">
          TBO reference number <span className="font-normal">(required)</span>
        </label>
        <input
          type="text"
          value={tboDgcaReference}
          onChange={(e) => setTboDgcaReference(e.target.value)}
          placeholder="e.g. TBO-CASE-12345"
          disabled={tboDgcaSubmitting}
          className="w-full border border-red-200 rounded-xl px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-60"
        />
      </div>
      <button
        type="button"
        onClick={onConfirmTboDgca}
        disabled={!dgcaConfirmReady(tboDgcaNote, tboDgcaReference) || tboDgcaSubmitting}
        className="w-full rounded-xl bg-red-600 px-4 py-2.5 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition-colors"
      >
        {tboDgcaSubmitting ? "Saving…" : "Confirm TBO Refund"}
      </button>
      {!dgcaConfirmReady(tboDgcaNote, tboDgcaReference) && (
        <p className="text-[11px] text-red-700">
          Fill in both boxes to enable this button.
        </p>
      )}
    </div>
  );
}

// Notify option -> the push notification_type it sends, to spot a repeat.
// The DGCA refund confirmation goes out as a fee quote (₹0 charge).
const NOTIFY_OPTION_PUSH_TYPE = {
  DGCA_REFUND_CONFIRMATION: "CANCELLATION_FEE_QUOTED",
};

// Pushes this booking's customer already received (ticket.customer_notifications,
// oldest first), and the last one of a given type if any.
export function lastSentOfType(notifications, pushType) {
  const list = Array.isArray(notifications) ? notifications : [];
  return [...list].reverse().find((n) => n?.notification_type === pushType) || null;
}

function formatSentAt(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

// TBO's API applies no DGCA waiver, so the agent must raise it with TBO and
// record both what TBO agreed and TBO's case reference (the server requires it).
export function dgcaConfirmReady(note, reference) {
  return !!String(note || "").trim() && String(reference || "").trim().length >= 3;
}

// DGCA free-look refund: the fare minus promo and our non-refundable
// convenience fee (computeRefundBreakdown's DGCA branch). No airline charge.
export function dgcaMaxRefund(booking) {
  if (!booking) return 0;
  const totalFare = Number(booking.totalfare) || 0;
  const convFee = Number(booking.convenience_fee) || 0;
  const promo =
    Number(booking.applied_promo_code) === 1 ? Number(booking.applied_promo_code_amount) || 0 : 0;
  return Math.max(0, Math.round((totalFare - promo - convFee) * 100) / 100);
}

// The other flights' tickets of a whole-trip request, each priced by the same
// read-only breakdown endpoint against its own booking. A failure on one keeps
// its row (marked) rather than hiding the trip total silently.
export async function fetchSiblingBreakdowns(relatedTickets) {
  if (!Array.isArray(relatedTickets) || relatedTickets.length === 0) return [];
  return Promise.all(
    relatedTickets.map(async (t) => {
      try {
        const res = await api.post("/admin/tickets/refund-breakdown", { ticket_id: Number(t.ticket_id) });
        return { ticket_id: t.ticket_id, route: t.route, data: res.data?.success ? res.data.data : null };
      } catch {
        return { ticket_id: t.ticket_id, route: t.route, data: null };
      }
    })
  );
}

// Every flight of a whole-trip request, in trip order (the outbound ticket was
// raised first, so it has the lower id), each with its own breakdown.
export function wholeTripFlights({ thisTicketId, thisRoute, thisData, siblings }) {
  return [
    { ticketId: Number(thisTicketId), route: thisRoute, data: thisData, isThis: true },
    ...siblings.map((sb) => ({ ticketId: Number(sb.ticket_id), route: sb.route, data: sb.data, isThis: false })),
  ]
    .sort((x, y) => x.ticketId - y.ticketId)
    .map((f, i) => ({ ...f, label: `Flight ${i + 1}` }));
}

// Step 2 for a whole-trip request: one line per flight plus the trip total, in
// plain words. Each flight is still cancelled and refunded on its own.
export function WholeTripBreakdown({ thisTicketId, thisRoute, thisData, siblings, passengerCount, formatINR }) {
  const amounts = (data) => ({
    paid: Number(data?.paid?.total_fare) || 0,
    kept: Number(data?.refund_split?.total_forfeited) || 0,
    back: Number(data?.refund_split?.total_customer_receives) || 0,
  });
  const rows = wholeTripFlights({ thisTicketId, thisRoute, thisData, siblings }).map((f) => ({
    ...f,
    ok: f.data != null,
    ...amounts(f.data),
  }));
  const allOk = rows.every((r) => r.ok);
  const sum = (k) => Math.round(rows.reduce((acc, r) => acc + r[k], 0) * 100) / 100;

  return (
    <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-4 space-y-2" data-testid="whole-trip-breakdown">
      <div className="text-xs font-bold uppercase tracking-wide text-indigo-900">
        Refund for the whole trip{passengerCount ? ` (${passengerCount} passenger${passengerCount === 1 ? "" : "s"})` : ""}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500">
            <th className="font-medium pb-1">Flight</th>
            <th className="font-medium pb-1 text-right">Customer paid</th>
            <th className="font-medium pb-1 text-right">We keep</th>
            <th className="font-medium pb-1 text-right">Customer gets back</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.ticketId} className="border-t border-indigo-100">
              <td className="py-1">
                {r.label}
                <span className="block text-[11px] text-gray-500">{r.route || `Ticket #${r.ticketId}`}</span>
              </td>
              {r.ok ? (
                <>
                  <td className="py-1 text-right tabular-nums">{formatINR(r.paid)}</td>
                  <td className="py-1 text-right tabular-nums">{formatINR(r.kept)}</td>
                  <td className="py-1 text-right tabular-nums font-semibold">{formatINR(r.back)}</td>
                </>
              ) : (
                <td colSpan={3} className="py-1 text-right text-xs text-red-700">Could not load this flight&apos;s amounts</td>
              )}
            </tr>
          ))}
          <tr className="border-t-2 border-indigo-200 font-bold text-gray-900">
            <td className="pt-1.5">Whole trip{allOk ? "" : " (some amounts missing)"}</td>
            <td className="pt-1.5 text-right tabular-nums">{formatINR(sum("paid"))}</td>
            <td className="pt-1.5 text-right tabular-nums">{formatINR(sum("kept"))}</td>
            <td className="pt-1.5 text-right tabular-nums">{formatINR(sum("back"))}</td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-indigo-900/80">
        The customer is cancelling both flights. They see one total and confirm once. Each flight is
        cancelled and refunded on its own. Details for each flight are below.
      </p>
    </div>
  );
}

// Accordion step. The current step is open; every other step that is not
// finished can be opened by its title (a step ahead of the current one, e.g.
// Review / Confirm on a DGCA ticket, is still usable, just shown faded).
// Finished steps stay a single line on purpose: re-opening a finished step
// would offer its actions again (a second "Cancel Booking & Process Refund").
function StepCard({ stepNum, activeStep, title, children, forceOpen = false }) {
  const done = !forceOpen && activeStep > stepNum;
  const current = activeStep === stepNum;
  const locked = !forceOpen && activeStep < stepNum;
  const [open, setOpen] = useState(current);
  useEffect(() => {
    if (current) setOpen(true);
  }, [current]);

  if (done) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-2.5 flex items-center gap-2">
        <span className="text-green-500 font-bold text-sm">✓</span>
        <span className="text-xs font-bold uppercase tracking-wide text-green-700">Step {stepNum}</span>
        <span className="text-sm text-green-700 font-medium">{title}</span>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border overflow-hidden transition-all ${
        current ? "border-blue-300 bg-white shadow-lg" : locked && !open ? "opacity-60 border-gray-200 bg-white" : "border-gray-200 bg-white"
      }`}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-label={`${open ? "Hide" : "Open"} step ${stepNum}`}
        onClick={() => setOpen((o) => !o)}
        className={`w-full px-4 py-3 flex items-center gap-2 text-left ${open ? "border-b" : ""} ${
          current ? "bg-blue-600" : "bg-gray-50 hover:bg-gray-100"
        }`}
      >
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
            current ? "bg-white text-blue-700" : "bg-gray-200 text-gray-700"
          }`}
        >
          Step {stepNum}
        </span>
        <span className={`text-sm font-semibold ${current ? "text-white" : "text-gray-700"}`}>{title}</span>
        <span className={`ml-auto text-xs ${current ? "text-white/90" : "text-gray-500"}`}>
          {locked && !open ? "Earlier step not finished · " : ""}
          {open ? "Hide ▴" : "Open ▾"}
        </span>
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CancellationPanel({
  ticket,
  ticket_id,
  booking,
  passengersWithIds,
  selectedPassengers,
  cancelType,
  setCancelType,
  cancelTypeLabel,
  setCancelTypeLabel,
  partialCancelSelectionError,
  cancellationTypeSelectOptions,
  formatINR,
  onTicketRefresh,
  // Whole Trip shown as one ticket: sending to the customer is one shared step
  // for both flights (WholeTripSendCard), so this flight's own Step 3 is hidden
  // before cancellation, and Step 4 shows only when a manual cancel is needed.
  wholeTrip = false,
}) {
  const [cancellationCharges, setCancellationCharges] = useState(null);
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [chargesAttempted, setChargesAttempted] = useState(false);
  // Persistent (not a toast) — set whenever the backend reports it cleared a
  // stale charge because the passenger selection changed since it was saved.
  // Stays visible until the agent's next "Get Fee" call resolves (see
  // handleGetCharges), not on a timer.
  const [previousChargeInvalidated, setPreviousChargeInvalidated] = useState(null);
  // Persistent (not a toast) — set on a failed "Get Fee" fetch with the
  // specific reason (auth/token, TBO transport, non-success ResponseStatus, or
  // client-side timeout). Cleared at the start of the next fetch attempt, not
  // on a timer.
  const [chargesFetchError, setChargesFetchError] = useState(null);

  const [cancelRemarks, setCancelRemarks] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  // Set when processing was refused because the charges snapshot is missing or
  // older than the server's TTL. Step 1 is usually collapsed by then, so Step 4
  // offers the refresh itself instead of leaving the agent with no way back.
  const [chargesNeedRefresh, setChargesNeedRefresh] = useState(null);

  const [manualCharge, setManualCharge] = useState("");
  const [manualRefund, setManualRefund] = useState("");
  // Engine preview for the charge being entered (refund-breakdown agent_quote):
  // the same refund the save stores and the payout sends.
  const [refundPreview, setRefundPreview] = useState(null);
  const [refundPreviewLoading, setRefundPreviewLoading] = useState(false);
  // The preview is informational only: the server recomputes the refund on
  // save, so a failed preview must never block Save Charge & Notify Customer.
  const [refundPreviewFailed, setRefundPreviewFailed] = useState(false);
  const previewTimer = useRef(null);
  const previewSeq = useRef(0);
  const [chargeSubmitting, setChargeSubmitting] = useState(false);
  const [chargeSubmitted, setChargeSubmitted] = useState(false);
  const [chargeApiError, setChargeApiError] = useState(null);
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  // The panel only has booking.totalfare available (no per-passenger fare
  // breakdown), so this is a conservative client-side bound for both full and
  // partial cancellations. The backend enforces the precise bound (pro-rated
  // per selected passengers for a partial cancellation) authoritatively.
  const manualChargeFareBasis = Number(booking?.totalfare) || 0;
  const manualChargeNum = parseFloat(manualCharge);
  const manualChargeExceedsFare =
    manualChargeFareBasis > 0 &&
    Number.isFinite(manualChargeNum) &&
    manualChargeNum > manualChargeFareBasis;

  // ── Per-passenger charge entry (audit finding #9 / recommendation #8) ─────
  // Only relevant for a partial cancellation where the cancelled passengers
  // span more than one fare type (adult/child/infant) — a single flat charge
  // can't reflect genuinely different per-type penalties in that case. When
  // all cancelled passengers share one type (or it's a full cancellation),
  // the existing single aggregate input above is used unchanged.
  const [passengerCharges, setPassengerCharges] = useState({});
  // Backend-reported violations (PASSENGER_CHARGE_EXCEEDS_FARE), keyed by
  // passenger_id, from the last submit attempt — lets every affected
  // passenger's input show the "exceeds fare" hint at once, rather than only
  // ever finding out about the first one client-side validation catches.
  const [passengerChargeViolations, setPassengerChargeViolations] = useState({});
  const cancelledPassengers =
    cancelType === 2
      ? (passengersWithIds || []).filter((p) => (selectedPassengers || []).includes(p.passenger_id))
      : [];
  const hasMixedPaxTypes =
    cancelType === 2 && new Set(cancelledPassengers.map((p) => p.pax_type)).size > 1;
  const passengerChargesTotal = Object.values(passengerCharges).reduce(
    (sum, v) => sum + (Number.isFinite(parseFloat(v)) ? parseFloat(v) : 0),
    0
  );
  const allPassengerChargesValid =
    hasMixedPaxTypes &&
    cancelledPassengers.length > 0 &&
    cancelledPassengers.every((p) => {
      const v = parseFloat(passengerCharges[p.passenger_id]);
      return passengerCharges[p.passenger_id] !== undefined &&
        passengerCharges[p.passenger_id] !== "" &&
        Number.isFinite(v) &&
        v >= 0;
    });
  const passengerChargeExceedsFare = (p) => {
    if (passengerChargeViolations[p.passenger_id]) return true;
    const fare = getPassengerFareAmount(p);
    const v = parseFloat(passengerCharges[p.passenger_id]);
    return fare > 0 && Number.isFinite(v) && v > fare;
  };
  const anyPassengerChargeExceedsFare = cancelledPassengers.some(passengerChargeExceedsFare);
  const [tboRefundedAmount, setTboRefundedAmount] = useState("");
  const [autoBreakdown, setAutoBreakdown] = useState(null);
  const [autoBreakdownLoading, setAutoBreakdownLoading] = useState(false);
  const [autoBreakdownError, setAutoBreakdownError] = useState(null);
  // Whole-trip request split across tickets (one per flight): the other
  // flights' breakdowns, so the agent sees the trip the customer asked to cancel.
  const [siblingBreakdowns, setSiblingBreakdowns] = useState([]);

  const [tboReference, setTboReference] = useState("");
  const [tboRefLoading, setTboRefLoading] = useState(false);

  const [notifyType, setNotifyType] = useState("");
  const [notifyRefundAmount, setNotifyRefundAmount] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // ── DGCA / TBO confirmation gate ─────────────────────────────────────────
  // Per TBO's own documented DGCA look-in process, a DGCA-eligible cancellation
  // must never be cancelled/refunded until an agent has separately checked with
  // TBO and confirmed the airline will honor the full refund. The backend now
  // refuses to execute (409 TBO_DGCA_CONFIRMATION_REQUIRED) until
  // ticket.tbo_dgca_confirmed is set — this UI mirrors that gate so the agent
  // sees it before trying, for both full and partial cancellations alike.
  const [tboDgcaNote, setTboDgcaNote] = useState("");
  const [tboDgcaReference, setTboDgcaReference] = useState("");
  const [tboDgcaSubmitting, setTboDgcaSubmitting] = useState(false);

  // "Done" for Step 1 means a genuinely usable, actionable result exists — a real
  // TBO RefundAmount for a full cancellation — not merely that a fetch attempt
  // resolved. cancellationCharges is also non-null on a TBO failure (fare-rule
  // fallback data) and on every partial cancellation (which never has a
  // RefundAmount at all), and those are exactly the cases where the agent still
  // needs Step 1's content (manual entry, suggestion, banners) to stay visible,
  // not collapse into a checkmark. The already-saved-a-charge case is handled
  // separately and correctly by the cancellation_status check above (charges_
  // notified), once updateCancellationCharge's save completes and the ticket
  // refetches — this flag only ever needs to cover the pre-save fetch state.
  const hasLocalCharges = cancellationCharges?.charges?.RefundAmount != null;
  // Reload-safe signal for "a charge was genuinely saved" — cancellation_charge
  // is only ever written by updateCancellationCharge, never by the charges-fetch/
  // suggestion step, so its presence (not just cancellation_status) is what
  // distinguishes an actual saved charge from a mere "quoted" suggestion.
  const hasSavedCharge = ticket?.cancellation_charge != null;
  const activeStep = deriveActiveStep(ticket?.cancellation_status, hasLocalCharges, hasSavedCharge);
  const isDone = activeStep >= 5;
  const awaitingCustomer = AWAITING_CUSTOMER_STATUSES.includes(ticket?.cancellation_status || "");

  // Inline prop from the page, so keep the latest in a ref rather than restarting
  // the interval on every render.
  const refreshRef = useRef(onTicketRefresh);
  refreshRef.current = onTicketRefresh;
  useEffect(() => {
    if (!awaitingCustomer) return undefined;
    const id = setInterval(() => {
      Promise.resolve(refreshRef.current?.()).catch(() => {});
    }, AWAITING_CUSTOMER_POLL_MS);
    return () => clearInterval(id);
  }, [awaitingCustomer]);
  // DGCA free-look: once TBO is confirmed, the agent sends the full refund
  // (₹0 charge) to the customer for Accept / Decline, the same way a fee quote
  // is sent; their Accept auto-cancels. Until it is sent, Step 3 offers that.
  const isDgcaTicket = isDgcaEligibleTicket(ticket);
  const dgcaTboConfirmed = Number(ticket?.tbo_dgca_confirmed) === 1;
  const dgcaRefundNotYetSent =
    isDgcaTicket &&
    !["charges_notified", "customer_accepted", "customer_declined", ...POST_AIRLINE_CANCEL_STATUSES].includes(
      ticket?.cancellation_status || ""
    );
  const notifyStepOpen =
    isNotifyStepAvailable(ticket?.cancellation_status, hasSavedCharge) ||
    (isDgcaTicket && dgcaRefundNotYetSent);
  const notifyOptions =
    isDgcaTicket && dgcaRefundNotYetSent
      ? [{ value: DGCA_REFUND_NOTIFY, label: "Refund for confirmation, no cancellation fee (Accept / Reject)" }]
      : notifyOptionsForStatus(ticket?.cancellation_status);
  const dgcaNotifyNeedsTboNote = notifyType === DGCA_REFUND_NOTIFY && !dgcaTboConfirmed;
  const sentNotifications = Array.isArray(ticket?.customer_notifications) ? ticket.customer_notifications : [];

  // Mirrors the backend gate (executeCancellation.service.js / processTicketCancellation):
  // a DGCA-eligible ticket cannot be processed until an agent has recorded that TBO
  // confirmed the full refund. Applies identically to full and partial cancellations.
  const dgcaBlocksExecution =
    isDgcaEligibleTicket(ticket) && Number(ticket?.tbo_dgca_confirmed) !== 1;

  const CANCELLATION_TYPE_SELECT_ALL = [
    { cancelType: 1, label: "Flight Cancelled by Airline (Full)" },
    { cancelType: 1, label: "Flight Cancelled by Passenger (Full)" },
    { cancelType: 2, label: "Partial Cancellation (Per Passenger)" },
    { cancelType: 2, label: "Partial Cancellation (Per Passenger Per Sector)" },
  ];
  const typeOptions = cancellationTypeSelectOptions || CANCELLATION_TYPE_SELECT_ALL;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const tboCancellationType =
    typeOptions.find((o) => o.label === cancelTypeLabel)?.tboCancellationType ?? 0;

  const handleGetCharges = async () => {
    if (cancelType === 2 && partialCancelSelectionError) {
      toast.error(partialCancelSelectionError);
      return;
    }
    setChargesAttempted(true);
    setCancellationLoading(true);
    // Retrying is what dismisses the previous failure banner (per spec — not a
    // timer); it's replaced by nothing (success) or a fresh banner (another
    // failure) once this attempt resolves.
    setChargesFetchError(null);
    try {
      const body = {
        cancel_type: cancelType,
        tbo_cancellation_type: tboCancellationType,
        remarks: `[${cancelTypeLabel}] ${cancelRemarks || ""}`.trim(),
        ...(cancelType === 2
          ? {
              passenger_ids: selectedPassengers.filter((id) =>
                passengersWithIds.some((p) => p.passenger_id === id)
              ),
            }
          : {}),
      };
      const res = await api.post(`/admin/tickets/${ticket_id}/cancellation-charges`, body);
      const chargesData = res.data?.data ?? null;
      setCancellationCharges(chargesData);
      setChargesNeedRefresh(null);
      // Reflects the backend's current-truth each call: set when this fetch just
      // cleared a stale charge for the old selection, cleared once a fetch comes
      // back without that flag (e.g. after the agent saves a charge for the new
      // selection and nothing is stale anymore).
      if (chargesData?.previous_charge_invalidated) {
        setPreviousChargeInvalidated({ amount: chargesData.previous_charge_amount });
        // The stale charge behind these inputs no longer applies to the new
        // selection — clear them so the agent can't hit Save with old figures
        // that were typed in before the selection changed.
        setManualCharge("");
        requestRefundPreview(""); // clears the refund and drops any pending preview
        setPassengerCharges({});
      } else {
        setPreviousChargeInvalidated(null);
      }
      const autoRefund = chargesData?.charges?.RefundAmount;
      if (autoRefund != null) {
        setTboRefundedAmount(String(Number(autoRefund)));
      }
      // Pre-fill the airline's own charge so the agent can save it and notify
      // the customer straight away. Nothing else saves a charge after a
      // successful fetch, and Notify stays locked until one is saved.
      const tboCharge = chargesData?.charges?.CancellationCharge;
      if (cancelType === 1 && tboCharge != null && !chargesData?.previous_charge_invalidated) {
        setManualCharge(String(Number(tboCharge)));
        requestRefundPreview(Number(tboCharge), { immediate: true });
      }
      toast.success(res.data?.message || "Charges received from airline");
    } catch (err) {
      // Even on a TBO failure (full cancellation), the backend still returns a
      // fare-rule suggestion in the error body — capture it so the manual-entry
      // fallback below can show it instead of leaving the agent with just a toast.
      const errData = err?.response?.data?.data ?? null;
      if (errData) {
        setCancellationCharges(errData);
      }
      // Persistent banner instead of a toast — see classifyChargesFetchError.
      setChargesFetchError(classifyChargesFetchError(err));
    } finally {
      setCancellationLoading(false);
    }
  };

  const handleShowBreakdown = async () => {
    setAutoBreakdownLoading(true);
    setAutoBreakdownError(null);
    try {
      const payload = { ticket_id: Number(ticket_id) };
      const tboRef = parseFloat(tboRefundedAmount);
      if (Number.isFinite(tboRef) && tboRef >= 0) payload.tbo_refunded_amount = tboRef;
      const airlineCharge = cancellationCharges?.charges?.CancellationCharge;
      if (airlineCharge != null) payload.tbo_cancellation_charge = Number(airlineCharge);
      const res = await api.post("/admin/tickets/refund-breakdown", payload);
      if (res.data?.success && res.data?.data) {
        setAutoBreakdown(res.data.data);
        setSiblingBreakdowns(await fetchSiblingBreakdowns(ticket?.customer_request?.related_tickets));
      } else {
        const msg = res.data?.message || "Could not compute breakdown. Try again.";
        setAutoBreakdownError(msg);
        toast.error(msg);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Could not compute breakdown.";
      setAutoBreakdownError(msg);
      toast.error(msg);
    } finally {
      setAutoBreakdownLoading(false);
    }
  };

  // Asks the server engine what this charge refunds. Debounced while typing;
  // only the latest request's answer is kept.
  const requestRefundPreview = (charge, { immediate = false } = {}) => {
    clearTimeout(previewTimer.current);
    const chargeNum = Number(charge);
    if (charge === "" || charge == null || !Number.isFinite(chargeNum) || chargeNum < 0) {
      previewSeq.current += 1;
      setRefundPreview(null);
      setRefundPreviewLoading(false);
      setRefundPreviewFailed(false);
      setManualRefund("");
      return;
    }
    setRefundPreviewLoading(true);
    setRefundPreviewFailed(false);
    const run = async () => {
      const seq = ++previewSeq.current;
      try {
        const res = await api.post("/admin/tickets/refund-breakdown", {
          ticket_id: Number(ticket_id),
          tbo_cancellation_charge: chargeNum,
        });
        if (seq !== previewSeq.current) return;
        const q = res?.data?.data?.agent_quote ?? null;
        setRefundPreview(q);
        setManualRefund(q ? String(q.refundAmount) : "");
        setRefundPreviewFailed(!q);
      } catch {
        if (seq !== previewSeq.current) return;
        setRefundPreview(null);
        setManualRefund("");
        setRefundPreviewFailed(true);
      } finally {
        if (seq === previewSeq.current) setRefundPreviewLoading(false);
      }
    };
    if (immediate) run();
    else previewTimer.current = setTimeout(run, 400);
  };

  const handleUseSuggestedCharge = () => {
    const suggested = cancellationCharges?.suggested_charge;
    if (suggested == null) return;
    // Pre-fills only — the agent still has to review and hit "Save Charge &
    // Notify Customer" themselves, same as typing the number in by hand.
    setManualCharge(String(suggested));
    setChargeApiError(null);
    requestRefundPreview(suggested, { immediate: true });
  };

  // Does the actual save. `reason` is the override justification when
  // isTboOverride triggered the confirm modal — empty string otherwise.
  const submitCharge = async (reason) => {
    setChargeSubmitting(true);
    setPassengerChargeViolations({});
    try {
      const payload = {
        ticket_id,
        ...(reason ? { override_reason: reason } : {}),
        // Always send the selection for a partial cancellation, including when
        // every selected passenger shares one pax type (single aggregate input).
        // Omitting it used to let the save erase pending_passenger_ids, so a
        // customer Accept cancelled the WHOLE booking with a 1-passenger charge.
        ...(cancelType === 2
          ? { passenger_ids: cancelledPassengers.map((p) => p.passenger_id) }
          : {}),
      };
      if (hasMixedPaxTypes) {
        payload.passenger_charges = cancelledPassengers.map((p) => ({
          passenger_id: p.passenger_id,
          charge: Number(passengerCharges[p.passenger_id]),
        }));
        // refund_amount_calculated intentionally omitted — the backend always
        // recomputes it server-side when the booking is present (same as the
        // single-aggregate path), and there's no simple client-side pro-ration
        // preview for a per-passenger breakdown worth sending here.
      } else {
        payload.cancellation_charge = Number(manualCharge);
        // Optional: the backend recomputes the refund from the charge anyway.
        if (manualRefund !== "") payload.refund_amount_calculated = Number(manualRefund);
      }
      await api.post("/admin/tickets/update-cancellation-charge", payload);
      toast.success("Charge saved. Customer notified.");
      setChargeSubmitted(true);
      setOverrideReason("");
      await onTicketRefresh();
    } catch (err) {
      const backendMsg = err?.response?.data?.message;
      const code = err?.response?.data?.code;
      const violations = err?.response?.data?.violations;
      if (code === "PASSENGER_CHARGE_EXCEEDS_FARE" && Array.isArray(violations)) {
        // Mark every affected passenger's input at once — the backend now
        // reports all violations from a single submit attempt, not just the
        // first one it hits, so there's no need to fix-and-resubmit repeatedly.
        const next = {};
        violations.forEach((v) => {
          next[v.passenger_id] = { charge: v.charge, passenger_fare: v.passenger_fare };
        });
        setPassengerChargeViolations(next);
        setChargeApiError(backendMsg);
      } else if (code === "CHARGE_EXCEEDS_FARE" && backendMsg) {
        // Stale frontend fare data (e.g. booking changed after load) — surface the
        // backend's specific message inline rather than a generic failure toast.
        setChargeApiError(backendMsg);
      } else if (code === "OVERRIDE_REASON_REQUIRED") {
        // The backend independently detected this as an override against a
        // persisted TBO charge our local isTboOverride check couldn't see (e.g.
        // fetched in an earlier session, not re-fetched this page load). Open
        // the same reason-confirmation modal instead of a dead-end error.
        setShowOverrideConfirm(true);
      } else {
        toast.error(backendMsg || err?.message || "Failed to save charge");
      }
    } finally {
      setChargeSubmitting(false);
    }
  };

  const handleManualCharge = () => {
    setChargeApiError(null);
    if (hasMixedPaxTypes) {
      if (!allPassengerChargesValid) {
        setChargeApiError("Enter a charge for every passenger before saving.");
        return;
      }
      if (anyPassengerChargeExceedsFare) {
        setChargeApiError(
          "One or more passenger charges exceed that passenger's own fare. Please re-check the amounts."
        );
        return;
      }
      // isTboOverride only ever applies to a full cancellation (cancelType === 1),
      // which can't be true here, so no override-modal branch is needed.
      submitCharge("");
      return;
    }
    if (manualChargeExceedsFare) {
      setChargeApiError(
        `Cancellation charge (₹${manualChargeNum.toLocaleString("en-IN")}) exceeds the total fare paid (₹${manualChargeFareBasis.toLocaleString("en-IN")}). Please re-check the amount.`
      );
      return;
    }
    if (isTboOverride) {
      // Don't submit yet — require a reason via the confirm modal first.
      setShowOverrideConfirm(true);
      return;
    }
    submitCharge("");
  };

  const handleSaveReference = async () => {
    if (!tboReference.trim()) return;
    setTboRefLoading(true);
    try {
      await api.post(`/admin/tickets/${ticket_id}/comments`, {
        comment: `[TBO REF] ${tboReference.trim()}`,
      });
      toast.success("Airline reference saved.");
      setTboReference("");
      await onTicketRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save reference.");
    } finally {
      setTboRefLoading(false);
    }
  };

  const handleConfirmTboDgca = async () => {
    if (!dgcaConfirmReady(tboDgcaNote, tboDgcaReference)) return;
    setTboDgcaSubmitting(true);
    try {
      await api.post(`/admin/tickets/${ticket_id}/confirm-tbo-dgca`, {
        note: tboDgcaNote.trim(),
        ...(tboDgcaReference.trim() ? { tbo_reference: tboDgcaReference.trim() } : {}),
      });
      toast.success("TBO confirmation recorded. You can now process this cancellation.");
      setTboDgcaNote("");
      setTboDgcaReference("");
      await onTicketRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not record TBO confirmation.");
    } finally {
      setTboDgcaSubmitting(false);
    }
  };

  const handleNotify = async () => {
    if (!notifyType) return;
    if (notifyType === DGCA_REFUND_NOTIFY) {
      setNotifyLoading(true);
      try {
        // The customer's Accept auto-cancels, which the server refuses until
        // TBO is confirmed, so record that first when it is still missing.
        if (!dgcaTboConfirmed) {
          await api.post(`/admin/tickets/${ticket_id}/confirm-tbo-dgca`, {
            note: tboDgcaNote.trim(),
            ...(tboDgcaReference.trim() ? { tbo_reference: tboDgcaReference.trim() } : {}),
          });
          setTboDgcaNote("");
          setTboDgcaReference("");
        }
        await api.post("/admin/tickets/update-cancellation-charge", { ticket_id, cancellation_charge: 0 });
        toast.success("Refund sent to the customer to Accept or Reject.");
        setNotifyType("");
        await onTicketRefresh();
      } catch (e) {
        toast.error(e?.response?.data?.message || "Could not send the refund to the customer.");
        await onTicketRefresh();
      } finally {
        setNotifyLoading(false);
      }
      return;
    }
    setNotifyLoading(true);
    try {
      await api.post("/admin/tickets/notify-customer", {
        ticket_id: Number(ticket_id),
        notification_type: notifyType,
        ...(notifyRefundAmount ? { refund_amount: Number(notifyRefundAmount) } : {}),
      });
      toast.success("Customer notified.");
      setNotifyType("");
      setNotifyRefundAmount("");
      await onTicketRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not send notification.");
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleExecute = async () => {
    if (cancelType === 2 && partialCancelSelectionError) {
      toast.error(partialCancelSelectionError);
      return;
    }
    setCancelSubmitting(true);
    try {
      const body = {
        cancel_type: cancelType,
        tbo_cancellation_type: tboCancellationType,
        remarks: `[${cancelTypeLabel}] ${cancelRemarks}`,
        bypass_state_check: true,
        ...(cancelType === 2
          ? {
              passenger_ids: selectedPassengers.filter((id) =>
                passengersWithIds.some((p) => p.passenger_id === id)
              ),
            }
          : {}),
      };
      const res = await api.post(`/admin/tickets/${ticket_id}/cancel`, body);
      if (res.data?.success && !res.data?.partial_success) {
        toast.success("Cancellation processed successfully");
        await onTicketRefresh();
        setCancellationCharges(null);
        setCancelRemarks("");
      } else if (res.data?.partial_success) {
        toast.error(
          "Cancelled but the cash refund to the customer's payment method failed. Ticket flagged for follow-up."
        );
        await onTicketRefresh();
        setCancellationCharges(null);
      } else {
        toast.error(res.data?.message || "Cancellation failed");
      }
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 207 && data?.partial_success) {
        toast.error(data?.message || "Partially completed — cash refund failed.");
        await onTicketRefresh();
        setCancellationCharges(null);
      } else if (data?.code === "CHARGES_STALE" || data?.code === "CHARGES_NOT_FETCHED") {
        setChargesNeedRefresh(data?.message || "Refresh the airline charges before processing.");
      } else if (status === 409 && data?.code === "TBO_DGCA_CONFIRMATION_REQUIRED") {
        toast.error(data?.message || "Confirm with TBO before processing this DGCA cancellation.");
        await onTicketRefresh();
      } else if (status === 409 && data?.code === "ALREADY_CANCELLED") {
        // Typically the customer accepted in the chatbot and it auto-cancelled
        // while this page was open: reload so Step 4 shows as done.
        toast.error(data?.message || "This booking has already been cancelled.");
        await onTicketRefresh();
      } else if (status === 409) {
        toast.error(data?.message || "Action already in progress. Please refresh.");
      } else {
        toast.error(data?.message || err?.message || "Cancellation failed");
      }
    } finally {
      setCancelSubmitting(false);
    }
  };

  // ── Saved TBO refs from history ──────────────────────────────────────────
  const savedTboRefs = (() => {
    const history = [
      ...(ticket?.ticket_history || []),
      ...(ticket?.history || []),
      ...(ticket?.comments || []),
    ];
    return history.filter((h) =>
      (h.comment || h.remarks || h.description || "").startsWith("[TBO REF]")
    );
  })();

  // ── Airline charge from charges response ─────────────────────────────────
  const airlineChargeFromResponse =
    cancellationCharges?.charges?.CancellationCharge != null
      ? Number(cancellationCharges.charges.CancellationCharge)
      : null;
  const tboRefundFromResponse =
    cancellationCharges?.charges?.RefundAmount != null
      ? Number(cancellationCharges.charges.RefundAmount)
      : null;

  // ── TBO override detection (full cancellation only) ───────────────────────
  // Audit recommendation #6: a TBO-fetched charge is pre-filled but freely
  // editable with only a passive hint that disappears on edit. This makes an
  // override an explicit, reasoned action instead of a silent edit. Backed by
  // this session's fetched TBO figure only — a charge persisted from an
  // earlier session that this page hasn't re-fetched won't be caught here, but
  // the backend independently checks against the persisted value and this
  // component's submit handler opens the same modal if that happens (see
  // submitCharge's catch block below).
  const isTboOverride =
    cancelType === 1 &&
    airlineChargeFromResponse != null &&
    Number.isFinite(manualChargeNum) &&
    Math.abs(manualChargeNum - airlineChargeFromResponse) >= 0.5;

  // ── Notification preview text ─────────────────────────────────────────────
  const notifyPreviewMap = {
    TICKET_CANCELLED_REFUND_PROCESSING:
      "Ticket Cancelled — Refund In Process. Your ticket is cancelled. Refund is under processing.",
    TICKET_CANCELLED: "Ticket Cancelled. Your ticket has been cancelled for your booking.",
    CANCELLATION_FEE_QUOTED:
      "Cancellation charges re-sent with Accept / Decline. The customer's response window restarts.",
    [DGCA_REFUND_NOTIFY]: `No airline cancellation fee applies to your booking. Refund: ${formatINR(
      dgcaMaxRefund(booking)
    )} (amount paid minus our non-refundable convenience fee). Tap to Accept or Decline.`,
    REFUND_ISSUED: notifyRefundAmount
      ? `Refund of ₹${Number(notifyRefundAmount).toLocaleString("en-IN")} has been issued for your booking.`
      : "Refund has been issued for your booking.",
  };

  // ── Step 3 card ──────────────────────────────────────────────────────────
  // Rendered inside the step list before cancellation, and below the done
  // banner after it, so "Refund issued" etc. can still be sent once the
  // booking is cancelled (the step list itself is hidden in the done state).
  const notifyStepCard = (
    <StepCard stepNum={3} activeStep={activeStep} title="Send to customer" icon="🔔" forceOpen={notifyStepOpen}>
      <p className="text-xs text-gray-500 -mt-1">
        Send a push notification to the customer&apos;s app. Save the airline reference number first if you have one.
      </p>
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 space-y-1.5">
        <p className="text-xs font-semibold text-gray-700">Already sent to the customer</p>
        {sentNotifications.length === 0 ? (
          <p className="text-[11px] text-gray-500">Nothing sent for this booking yet.</p>
        ) : (
          <ol className="space-y-1">
            {sentNotifications.map((n, i) => (
              <li key={`${n.notification_type}-${i}`} className="text-[11px] text-gray-700">
                <span className="font-semibold">{i + 1}. {n.title || n.notification_type}</span>
                <span className="text-gray-500"> · {formatSentAt(n.sent_at)}</span>
                {!n.delivered && <span className="text-red-600 font-semibold"> · not delivered</span>}
                <div className="text-gray-500">{n.body}</div>
              </li>
            ))}
          </ol>
        )}
      </div>
      {isDgcaTicket && dgcaRefundNotYetSent && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 space-y-2">
          <p className="text-xs font-semibold text-amber-900">
            DGCA free-look: ask the customer to confirm their refund of {formatINR(dgcaMaxRefund(booking))}
          </p>
          <p className="text-[11px] text-amber-800">
            No airline cancellation fee. Amount paid minus our non-refundable convenience fee. The
            customer gets Accept / Decline in the app and chat; Accept cancels the booking and sends
            the refund automatically.
          </p>
          <p className="text-[11px] font-semibold text-amber-900">
            Choose &ldquo;Refund for confirmation&rdquo; below and click Send Notification.
          </p>
        </div>
      )}
      {/* Airline reference — useful to save before notifying */}
      <div className="space-y-2 pb-3 border-b border-gray-100">
        <div className="text-xs font-semibold text-gray-500">
          Airline Reference Number (optional)
        </div>
        {savedTboRefs.length > 0 && (
          <div className="space-y-1">
            {savedTboRefs.map((h, i) => (
              <div
                key={i}
                className="rounded-lg bg-green-50 border border-green-200 px-2.5 py-1.5 text-xs font-mono text-green-800"
              >
                {(h.comment || h.remarks || h.description || "").replace("[TBO REF] ", "")}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="e.g. AIR-2026-XXXXXX"
            value={tboReference}
            onChange={(e) => setTboReference(e.target.value)}
            className="flex-1 border rounded-xl px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-black"
          />
          <button
            type="button"
            onClick={handleSaveReference}
            disabled={!tboReference.trim() || tboRefLoading}
            className="rounded-xl bg-gray-900 text-white px-3 py-2 text-xs font-semibold disabled:opacity-50 whitespace-nowrap hover:opacity-90"
          >
            {tboRefLoading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Notification */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-gray-500">
          What do you want to tell the customer?
        </div>
        <select
          value={notifyType}
          onChange={(e) => setNotifyType(e.target.value)}
          className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
        >
          <option value="">Choose a message to send…</option>
          {notifyOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {notifyOptions.length === 0 && (
          <p className="text-[11px] text-gray-500">
            {isDgcaEligibleTicket(ticket)
              ? "DGCA free-look case: use the refund confirmation above to ask the customer. The cancellation and refund messages become available here after the booking is cancelled."
              : "Save a cancellation charge first. Cancellation and refund messages become available after the booking is cancelled with the airline."}
          </p>
        )}

        {notifyType === "REFUND_ISSUED" && (
          <input
            type="number"
            placeholder="Refund amount (₹)"
            value={notifyRefundAmount}
            onChange={(e) => setNotifyRefundAmount(e.target.value)}
            className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
          />
        )}

        {notifyType && (
          <div className="rounded-xl bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-800">
            <span className="font-semibold">Preview: </span>
            {notifyPreviewMap[notifyType] || notifyType}
          </div>
        )}

        {notifyType && lastSentOfType(sentNotifications, NOTIFY_OPTION_PUSH_TYPE[notifyType] || notifyType) && (
          <div className="rounded-xl bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900">
            ⚠ This message was already sent on{" "}
            {formatSentAt(lastSentOfType(sentNotifications, NOTIFY_OPTION_PUSH_TYPE[notifyType] || notifyType).sent_at)}.
            Sending it again gives the customer a duplicate.
          </div>
        )}

        {dgcaNotifyNeedsTboNote && (
          <div className="space-y-2">
            <label className="text-xs text-red-700 block font-medium">
              What did TBO confirm? <span className="font-normal">(required, recorded before sending)</span>
            </label>
            <textarea
              value={tboDgcaNote}
              onChange={(e) => setTboDgcaNote(e.target.value)}
              rows={2}
              placeholder="e.g. TBO confirmed the airline will refund the full fare for this PNR"
              disabled={notifyLoading}
              className="w-full border border-red-200 rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-60"
            />
            <input
              type="text"
              value={tboDgcaReference}
              onChange={(e) => setTboDgcaReference(e.target.value)}
              placeholder="TBO reference number (required)"
              disabled={notifyLoading}
              className="w-full border border-red-200 rounded-xl px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-60"
            />
          </div>
        )}

        <button
          type="button"
          onClick={handleNotify}
          disabled={
            !notifyType || notifyLoading || (dgcaNotifyNeedsTboNote && !dgcaConfirmReady(tboDgcaNote, tboDgcaReference))
          }
          className="w-full bg-gray-900 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
        >
          {notifyLoading ? "Sending…" : "Send Notification"}
        </button>
        <p className="text-[10px] text-gray-400">
          Always make sure the refund has been fully processed before sending &ldquo;Refund Issued&rdquo;.
        </p>
      </div>
    </StepCard>
  );

  // Whole Trip shown as one ticket: Flight 1 (lower ticket id) carries the
  // request and the trip totals once; Flight 2 shows only its own flight.
  const wholeTripRole = ticket?.linked_ticket_id
    ? Number(ticket_id) > Number(ticket.linked_ticket_id) ? "second" : "first"
    : null;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
          Cancellation
        </span>
        {ticket?.cancellation_status && <StatusChip ticket={ticket} />}
      </div>

      {/* What the customer asked for: scope and passengers */}
      {wholeTripRole !== "second" && (
        <CustomerRequestCard request={ticket?.customer_request} sameTicketView={wholeTripRole === "first"} />
      )}

      {/* DGCA warning — shown whenever eligible */}
      <DgcaBanner
        ticket={ticket}
        booking={booking}
        formatINR={formatINR}
        tboDgcaNote={tboDgcaNote}
        setTboDgcaNote={setTboDgcaNote}
        tboDgcaReference={tboDgcaReference}
        setTboDgcaReference={setTboDgcaReference}
        tboDgcaSubmitting={tboDgcaSubmitting}
        onConfirmTboDgca={handleConfirmTboDgca}
      />

      {/* Fare summary — always pinned */}
      <FareSummary
        booking={booking}
        formatINR={formatINR}
        tripFare={ticket?.customer_request?.trip_fare}
        showTripTotal={wholeTripRole !== "second"}
      />

      {/* Step tracker */}
      <StepTracker activeStep={activeStep} isDgca={isDgcaTicket} wholeTrip={wholeTrip} />

      {/* Done state */}
      {isDone ? (
        <div className="space-y-3">
          <DoneBanner cancellationStatus={ticket?.cancellation_status} />
          <RefundSummaryCard
            refundSummary={ticket?.refund_summary}
            formatINR={formatINR}
          />
          {notifyStepOpen && notifyStepCard}
        </div>
      ) : (
        <div className="space-y-2">

          {/* ── Step 1: Get Charges ──────────────────────────────────────── */}
          {/* Save Charge & Notify lives in this step: keep it open after a
              successful fetch until a charge is actually saved, or the button
              collapses away with the step. */}
          <StepCard
            stepNum={1}
            activeStep={activeStep}
            title={isDgcaTicket ? "Check with TBO (free cancellation, no airline fee)" : "Get the airline's cancellation fee"}
            icon="🔍"
            forceOpen={hasLocalCharges && !hasSavedCharge}
          >
            {/* DGCA free-look: there is no airline fee to fetch or enter. The
                server only accepts a ₹0 charge, and only after TBO is confirmed,
                so the fee fetch / manual charge below was a dead end (409
                TBO_DGCA_CONFIRMATION_REQUIRED, then DGCA_CHARGE_MUST_BE_ZERO). */}
            {isDgcaTicket ? (
              <DgcaNoFeeSteps
                tboConfirmed={dgcaTboConfirmed}
                booking={booking}
                formatINR={formatINR}
                tripFare={ticket?.customer_request?.trip_fare}
                wholeTrip={wholeTrip}
                confirmForm={
                  <DgcaConfirmForm
                    tboDgcaNote={tboDgcaNote}
                    setTboDgcaNote={setTboDgcaNote}
                    tboDgcaReference={tboDgcaReference}
                    setTboDgcaReference={setTboDgcaReference}
                    tboDgcaSubmitting={tboDgcaSubmitting}
                    onConfirmTboDgca={handleConfirmTboDgca}
                  />
                }
              />
            ) : (
            <>
            <p className="text-xs text-gray-500 -mt-1">
              We&apos;ll check with the airline to find out their cancellation fee and the refund amount. This usually takes a few seconds.
            </p>

            {/* Stale-charge warning — persistent banner, not a toast. Set when the
                backend just cleared a saved charge because the passenger selection
                changed since it was saved (see getTicketCancellationCharges). Stays
                up until the agent's next "Get Fee" call resolves. */}
            {previousChargeInvalidated && (
              <div className="rounded-xl border border-red-300 bg-red-50 px-3 py-2.5">
                <p className="text-sm font-semibold text-red-800">
                  ⚠ Passenger selection changed since a charge of ₹{Number(previousChargeInvalidated.amount).toLocaleString("en-IN")} was saved for the previous selection.
                </p>
                <p className="text-xs text-red-700 mt-1">
                  That charge has been cleared — please get fees and save again for the current selection.
                </p>
              </div>
            )}

            {/* Originally quoted charge — shows for bot-escalated tickets where
                charge_estimate_source was already populated before this ticket ever
                reached the admin panel, so the agent isn't recomputing blind and
                risking a different figure than what the customer already saw. */}
            {ticket?.charge_estimate_source?.cancellationCharge != null && (() => {
              const src = ticket.charge_estimate_source;
              const sourceLabels = {
                tbo_full_cancellation: "Airline system (TBO), full cancellation",
                tbo_override: "Airline system (TBO), overridden by agent",
                mini_fare_rule_estimate: "Fare-rule estimate (auto, bot)",
                agent_fare_rule_manual: "Fare rule, entered manually by agent",
              };
              const sourceLabel = sourceLabels[src.source] || src.source || "Unknown";
              const capturedAtLabel = src.capturedAt
                ? new Date(src.capturedAt).toLocaleString("en-IN")
                : null;
              return (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 space-y-0.5">
                  <p className="text-xs font-semibold text-amber-800">
                    Originally quoted to customer: ₹{Number(src.cancellationCharge).toLocaleString("en-IN")}
                    {src.refundToCustomer != null && (
                      <> — refund ₹{Number(src.refundToCustomer).toLocaleString("en-IN")}</>
                    )}
                  </p>
                  <p className="text-[11px] text-amber-700">
                    Source: {sourceLabel}
                    {capturedAtLabel && <> · Captured {capturedAtLabel}</>}
                  </p>
                  <p className="text-[11px] text-amber-600">
                    If the number you compute below differs, verify with the customer before saving a different charge.
                  </p>
                </div>
              );
            })()}

            {/* Cancel type */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block font-medium">
                Cancellation Type
              </label>
              <select
                value={cancelTypeLabel}
                onChange={(e) => {
                  const label = e.target.value;
                  setCancelTypeLabel(label);
                  const row = typeOptions.find((o) => o.label === label);
                  if (row) setCancelType(row.cancelType);
                }}
                disabled={cancellationLoading}
                className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
              >
                {typeOptions.map((o) => (
                  <option key={o.label} value={o.label}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Partial passengers hint */}
            {cancelType === 2 && (() => {
              const validSelected = (selectedPassengers || []).filter((id) =>
                (passengersWithIds || []).some((p) => p.passenger_id === id)
              );
              const selectedNames = validSelected.map((id) => {
                const p = (passengersWithIds || []).find((p) => p.passenger_id === id);
                return p ? [p.title, p.first_name, p.last_name].filter(Boolean).join(" ").trim() : null;
              }).filter(Boolean);
              const unselectedNames = (passengersWithIds || [])
                .filter((p) => !validSelected.includes(p.passenger_id))
                .map((p) => [p.title, p.first_name, p.last_name].filter(Boolean).join(" ").trim());

              if (partialCancelSelectionError) {
                return (
                  <div className="rounded-xl px-3 py-2.5 text-xs bg-red-50 border border-red-200 text-red-700 space-y-1">
                    <p className="font-semibold">⚠ {partialCancelSelectionError}</p>
                    <p>Select the passengers to cancel from the passenger table above, then click the button below.</p>
                  </div>
                );
              }
              if (validSelected.length === 0) {
                return (
                  <div className="rounded-xl px-3 py-2.5 text-xs bg-amber-50 border border-amber-200 text-amber-800 space-y-1">
                    <p className="font-semibold">No passengers selected yet</p>
                    <p>Tick the passengers to cancel in the table above, then come back here.</p>
                  </div>
                );
              }
              return (
                <div className="rounded-xl px-3 py-2.5 text-xs bg-blue-50 border border-blue-200 text-blue-800 space-y-1.5">
                  <p className="font-semibold text-blue-900">Cancelling for {validSelected.length} passenger{validSelected.length > 1 ? "s" : ""}:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {selectedNames.map((n, i) => <li key={i} className="text-red-700 font-medium">{n} — will be cancelled</li>)}
                    {unselectedNames.map((n, i) => <li key={i} className="text-green-700">{n} — stays active</li>)}
                  </ul>
                </div>
              );
            })()}

            {/* Get Charges button */}
            <button
              type="button"
              disabled={
                cancellationLoading ||
                (cancelType === 2 && partialCancelSelectionError != null)
              }
              onClick={handleGetCharges}
              className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {cancellationLoading ? "Checking with airline…" : "Get Cancellation Fee from Airline"}
            </button>

            {/* Persistent failure banner — not a toast, stays until the agent
                retries (see handleGetCharges) or navigates away. Additive
                context above the manual-entry fallback, which still auto-reveals
                below exactly as before. */}
            {chargesFetchError && (
              <div className="rounded-xl border border-red-300 bg-red-50 px-3 py-2.5">
                <p className="text-sm font-semibold text-red-800">
                  ⚠ {chargesFetchError.title}
                </p>
                <p className="text-xs text-red-700 mt-1">{chargesFetchError.detail}</p>
              </div>
            )}

            {/* Result summary. Hidden on a failed fetch: the catch in
                handleGetCharges still stores the error body's fare-rule
                suggestion in cancellationCharges for the manual-entry box. */}
            {cancellationCharges != null && !chargesFetchError && (
              <div className="rounded-xl bg-green-50 border border-green-200 px-3 py-2.5 space-y-2">
                <div className="text-xs font-bold text-green-700 uppercase tracking-wide">
                  ✅ Airline responded
                </div>
                {airlineChargeFromResponse != null && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Airline will deduct</span>
                    <span className="font-bold text-red-600">
                      − {formatINR(airlineChargeFromResponse)}
                    </span>
                  </div>
                )}
                {tboRefundFromResponse != null && (
                  <div className="flex justify-between items-center text-sm">
                    {/* TBO's RefundAmount is what the airline returns to us. It
                        excludes our convenience fee and TBO's OtherCharges, so it
                        is not the customer's refund (that is worked out below). */}
                    <span className="text-gray-600">Airline refunds to Navigatio</span>
                    <span className="font-bold text-green-700">
                      {formatINR(tboRefundFromResponse)}
                    </span>
                  </div>
                )}
                {tboRefundFromResponse != null && (
                  <p className="text-[10px] text-green-700 pt-0.5">
                    Airline charge pre-filled below. Check the customer refund, then click Save Charge &amp; Notify Customer.
                  </p>
                )}
                <details className="pt-1">
                  <summary className="text-[10px] font-semibold text-gray-400 hover:text-gray-600 cursor-pointer">
                    Raw airline response
                  </summary>
                  <pre className="text-[10px] bg-gray-900 text-gray-100 rounded-lg p-2 overflow-x-auto max-h-40 mt-1">
                    {JSON.stringify(cancellationCharges.charges ?? cancellationCharges, null, 2)}
                  </pre>
                </details>
              </div>
            )}

            {/* Charge entry + Save & Notify. Shown after every fetch: when the
                airline returned charges it is pre-filled with them, otherwise the
                agent types the fee in. It used to be hidden whenever TBO returned
                a RefundAmount, and nothing else saves a charge, so the customer
                could never be notified. */}
            {chargesAttempted && (
              <div className="pt-2 border-t border-gray-100 space-y-2">
                <div>
                  {tboRefundFromResponse != null ? (
                    <p className="text-xs font-semibold text-green-700">
                      Airline charge filled in from the airline&apos;s response. Review it and notify the customer.
                    </p>
                  ) : (
                    <p className="text-xs font-semibold text-amber-700">
                      {cancelType === 2
                        ? "⚠ Partial cancellation charges are estimated from the fare rules, not fetched from the airline automatically."
                        : "⚠ Airline didn't return the charges automatically."}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-0.5">
                    {tboRefundFromResponse != null
                      ? "The refund amount is calculated automatically from the charge."
                      : "Enter the cancellation fee from the airline's email or TBO portal. The refund amount will be calculated automatically."}
                  </p>
                </div>

                {(() => {
                  const hoursUntilDep = cancellationCharges?.hours_until_departure;
                  const suggested = cancellationCharges?.suggested_charge;
                  const suggestedWindow = cancellationCharges?.suggested_charge_window;
                  const cancellationRules = cancellationCharges?.fare_rule?.cancellation || [];
                  if (hoursUntilDep == null && suggested == null && cancellationRules.length === 0) {
                    return null;
                  }
                  return (
                    <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 space-y-2">
                      {hoursUntilDep != null && (
                        <p className="text-xs text-blue-800">
                          {hoursUntilDep >= 0
                            ? `~${Math.round(hoursUntilDep)} hour(s) until departure.`
                            : `Departure was ~${Math.round(Math.abs(hoursUntilDep))} hour(s) ago.`}
                        </p>
                      )}
                      {hasMixedPaxTypes ? (
                        <p className="text-xs text-gray-600">
                          No automatic suggestion available for mixed passenger types — TBO&apos;s fare rule doesn&apos;t distinguish by passenger type, so a per-type figure can&apos;t be derived from it. Please review the raw fare rule below and enter each passenger&apos;s charge individually.
                        </p>
                      ) : suggested != null ? (
                        <div className="rounded-lg bg-white border border-blue-200 px-3 py-2 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-blue-900">
                              Suggested charge based on fare rule: ₹{Number(suggested).toLocaleString("en-IN")}
                            </p>
                            {suggestedWindow && (
                              <p className="text-[11px] text-gray-500 mt-0.5">Matched rule window: {suggestedWindow}</p>
                            )}
                            <p className="text-[11px] text-gray-500 mt-1">Please verify against the fare rule below before saving.</p>
                          </div>
                          <button
                            type="button"
                            onClick={handleUseSuggestedCharge}
                            disabled={chargeSubmitting || chargeSubmitted}
                            className="shrink-0 text-xs font-semibold text-blue-700 border border-blue-300 rounded-lg px-2 py-1 hover:bg-blue-100 disabled:opacity-50"
                          >
                            Use suggested amount
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-600">
                          No single applicable rate could be determined automatically (rule may be percentage-based or time window didn&apos;t match) — please review the fare rule below manually.
                        </p>
                      )}
                      {cancellationRules.length > 0 ? (
                        <ul className="space-y-0.5 border-t border-blue-100 pt-2">
                          {cancellationRules.map((r, i) => (
                            <li key={i} className="text-[11px] text-gray-700 flex justify-between gap-2">
                              <span>{r.window || "—"}</span>
                              <span className="font-medium">{r.penalty || "—"}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-[11px] text-gray-400 border-t border-blue-100 pt-2">
                          No fare rule data available for this booking.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {hasMixedPaxTypes ? (
                  <div className="space-y-2">
                    {cancelledPassengers.map((p) => {
                      const name = [p.title, p.first_name, p.last_name].filter(Boolean).join(" ").trim();
                      const violation = passengerChargeViolations[p.passenger_id];
                      const exceedsFare = passengerChargeExceedsFare(p);
                      // Prefer the backend's authoritative fare when a violation was
                      // reported for this passenger — it's the same figure the
                      // server actually checked against, not this client's estimate.
                      const fareAmount = violation ? violation.passenger_fare : getPassengerFareAmount(p);
                      return (
                        <div key={p.passenger_id}>
                          <label className="text-xs text-gray-500 mb-1 block">
                            {name || `Passenger ${p.passenger_id}`}{" "}
                            <span className="text-gray-400">({p.pax_type_label || "Unknown type"})</span> — Charge (₹)
                          </label>
                          <input
                            type="number"
                            value={passengerCharges[p.passenger_id] ?? ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setPassengerCharges((prev) => ({ ...prev, [p.passenger_id]: val }));
                              setChargeApiError(null);
                              // Clear only this passenger's stale violation flag —
                              // they're revising the value, let it be re-checked
                              // fresh on the next submit rather than staying stuck.
                              setPassengerChargeViolations((prev) => {
                                if (!prev[p.passenger_id]) return prev;
                                const next = { ...prev };
                                delete next[p.passenger_id];
                                return next;
                              });
                            }}
                            placeholder="e.g. 500"
                            disabled={chargeSubmitting || chargeSubmitted}
                            className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
                          />
                          {exceedsFare && (
                            <p className="text-[10px] text-red-600 mt-1 font-semibold">
                              Exceeds this passenger&apos;s fare (₹{fareAmount.toLocaleString("en-IN")}).
                            </p>
                          )}
                        </div>
                      );
                    })}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100 text-sm">
                      <span className="text-gray-600 font-medium">Total charge</span>
                      <span className="font-bold text-gray-900">₹{passengerChargesTotal.toLocaleString("en-IN")}</span>
                    </div>
                    <p className="text-[10px] text-gray-400">
                      Refund amount will be calculated automatically from the total once saved.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">
                        Airline Cancellation Charge (₹)
                      </label>
                      <input
                        type="number"
                        value={manualCharge}
                        onChange={(e) => {
                          const val = e.target.value;
                          setManualCharge(val);
                          setChargeApiError(null);
                          requestRefundPreview(val);
                        }}
                        placeholder="e.g. 500"
                        disabled={chargeSubmitting || chargeSubmitted}
                        className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">Amount the airline will deduct</p>
                      {manualChargeExceedsFare && (
                        <p className="text-[10px] text-red-600 mt-1 font-semibold">
                          Exceeds total fare paid (₹{manualChargeFareBasis.toLocaleString("en-IN")}).
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">
                        Total Refund Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={manualRefund}
                        readOnly
                        placeholder="Auto-calculated"
                        disabled={chargeSubmitting || chargeSubmitted}
                        className="w-full border rounded-xl px-3 py-2 text-sm outline-none bg-gray-50 text-gray-700 disabled:opacity-60"
                      />
                      {(() => {
                        const gc = booking?.is_giftcard_applied === true || Number(booking?.is_giftcard_applied) === 1
                          ? Number(booking?.applied_giftcard_amount) || 0 : 0;
                        const cashP = Math.max(0, (Number(booking?.totalfare) || 0) - (Number(booking?.wallet_points) || 0) - gc);
                        return (
                          <>
                            {refundPreviewLoading && (
                              <p className="text-[10px] text-gray-400 mt-1">Calculating refund…</p>
                            )}
                            {!refundPreviewLoading && refundPreviewFailed && manualCharge !== "" && (
                              <p className="text-[10px] text-amber-700 mt-1" data-testid="refund-preview-failed">
                                Couldn&apos;t preview the refund. You can still save: the refund is calculated on the server when you save.
                              </p>
                            )}
                            {!refundPreviewLoading && refundPreview && (
                              <p className="text-[10px] text-gray-500 mt-1" data-testid="refund-formula">
                                {refundFormulaText(refundPreview)}
                              </p>
                            )}
                            {gc > 0 ? (
                              <p className="text-[10px] text-purple-600 mt-1 font-medium">
                                {cashP === 0 ? "Full refund → customer's wallet (gift card booking)" : "Gift card portion → wallet, remainder → bank"}
                              </p>
                            ) : (
                              <p className="text-[10px] text-gray-400 mt-1">Calculated by the refund engine. This is what the customer is sent and paid.</p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
                {chargeApiError && (
                  <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 font-semibold">
                    {chargeApiError}
                  </div>
                )}
                {chargeSubmitted ? (
                  <div className="rounded-xl bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700 font-semibold">
                    ✅ Charge saved. Customer notified.
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={
                      chargeSubmitting ||
                      (hasMixedPaxTypes
                        ? !allPassengerChargesValid || anyPassengerChargeExceedsFare
                        : !manualCharge ||
                          refundPreviewLoading ||
                          Number(manualCharge) < 0 ||
                          manualChargeExceedsFare)
                    }
                    onClick={handleManualCharge}
                    className="w-full rounded-xl bg-blue-600 px-4 py-2 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                  >
                    {chargeSubmitting ? "Saving…" : wholeTrip ? "Save charge for this flight" : "Save Charge & Notify Customer"}
                  </button>
                )}

                {/* ── TBO override confirmation ───────────────────────────── */}
                {showOverrideConfirm && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-4">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">⚠️</span>
                        <h3 className="text-base font-bold text-gray-900">Confirm Override</h3>
                      </div>
                      <p className="text-sm text-gray-600">
                        You&apos;re saving a charge of{" "}
                        <strong>₹{Number.isFinite(manualChargeNum) ? manualChargeNum.toLocaleString("en-IN") : manualCharge}</strong>,
                        different from the airline&apos;s quoted charge of{" "}
                        <strong>
                          {airlineChargeFromResponse != null
                            ? `₹${Number(airlineChargeFromResponse).toLocaleString("en-IN")}`
                            : "the previously fetched amount"}
                        </strong>
                        . Please provide a reason for this override.
                      </p>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block font-medium">
                          Reason for override
                        </label>
                        <textarea
                          value={overrideReason}
                          onChange={(e) => setOverrideReason(e.target.value)}
                          rows={3}
                          placeholder="e.g. Airline waived the fee over phone, confirmed via reference #..."
                          className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
                        />
                      </div>
                      <div className="flex gap-3 pt-1">
                        <button
                          type="button"
                          onClick={() => setShowOverrideConfirm(false)}
                          className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Go Back
                        </button>
                        <button
                          type="button"
                          disabled={!overrideReason.trim() || chargeSubmitting}
                          onClick={() => {
                            const reason = overrideReason.trim();
                            setShowOverrideConfirm(false);
                            submitCharge(reason);
                          }}
                          className="flex-1 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
                        >
                          {chargeSubmitting ? "Saving…" : "Confirm Override & Save"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            </>
            )}
          </StepCard>

          {/* ── Step 2: Review ───────────────────────────────────────────── */}
          <StepCard stepNum={2} activeStep={activeStep} title="Check the refund" icon="🧮">
            <p className="text-xs text-gray-500 -mt-1">
              See exactly how much will be refunded and through which method (bank transfer, wallet, etc.).
            </p>

            {ticket?.status === "Closed" && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                This ticket is closed. The breakdown below reflects historical data.
              </div>
            )}

            {/* DGCA free-look: the airline charge is waived, so TBO's own refund
                figure does not apply and the engine ignores it. Show the refund
                the customer actually gets instead. */}
            {isDgcaEligibleTicket(ticket) ? (
              <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 space-y-0.5">
                <p className="font-semibold">
                  DGCA free-look refund: {formatINR(dgcaMaxRefund(booking))}
                </p>
                <p>
                  No airline cancellation charge applies. Fare paid minus our non-refundable
                  convenience fee. The airline&apos;s own refund figure is not used for this ticket.
                </p>
              </div>
            ) : (
            /* Airline confirmed refund (auto-filled from Step 1, editable) */
            <div>
              <label className="text-xs text-gray-500 mb-1 block font-medium">
                Refund Amount Confirmed by Airline (₹)
              </label>
              <input
                type="number"
                placeholder="Enter amount if known"
                value={tboRefundedAmount}
                onChange={(e) => setTboRefundedAmount(e.target.value)}
                className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black"
              />
              {tboRefundFromResponse != null && tboRefundedAmount === String(Number(tboRefundFromResponse)) ? (
                <p className="text-[10px] text-green-600 mt-1">
                  ✓ Auto-filled from airline response — you can change this if needed.
                </p>
              ) : (
                <p className="text-[10px] text-gray-400 mt-1">
                  Leave blank to estimate from stored fare data.
                </p>
              )}
            </div>
            )}

            <button
              type="button"
              onClick={handleShowBreakdown}
              disabled={autoBreakdownLoading}
              className="w-full bg-blue-600 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
            >
              {autoBreakdownLoading ? "Calculating…" : "Calculate Refund Breakdown"}
            </button>

            {autoBreakdownError && !autoBreakdown && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                {autoBreakdownError}
              </div>
            )}

            {autoBreakdown && siblingBreakdowns.length > 0 && (() => {
              const wtProps = {
                thisTicketId: ticket_id,
                thisRoute: ticket?.customer_request?.trip_fare?.legs?.find((l) => l.is_this_ticket)?.route,
                thisData: autoBreakdown,
                siblings: siblingBreakdowns,
              };
              return (
                <>
                  <WholeTripBreakdown
                    {...wtProps}
                    passengerCount={ticket?.customer_request?.passenger_count}
                    formatINR={formatINR}
                  />
                  {wholeTripFlights(wtProps).map((f) =>
                    f.data ? (
                      <RefundBreakdownPanel
                        key={f.ticketId}
                        data={f.data}
                        formatINR={formatINR}
                        title={`${f.label}${f.route ? ` · ${f.route}` : ""}`}
                        collapsible
                      />
                    ) : null
                  )}
                </>
              );
            })()}

            {autoBreakdown && siblingBreakdowns.length === 0 && (
              <RefundBreakdownPanel data={autoBreakdown} formatINR={formatINR} />
            )}
          </StepCard>

          {/* ── Step 3: Notify ───────────────────────────────────────────── */}
          {!wholeTrip && notifyStepCard}

          {/* ── Step 4: Execute ──────────────────────────────────────────── */}
          {/* DGCA free-look goes straight from Get Fee to Step 4 (no fee to save
              or notify), so the step must not look locked. On a whole trip the
              customer's Accept cancels both flights automatically; this flight's
              manual cancel shows only when that did not finish. */}
          {(!wholeTrip || (ticket?.cancellation_status === "customer_accepted" && ticket?.status === "Escalated")) && (
          <StepCard
            stepNum={4}
            activeStep={activeStep}
            title="Cancel and refund"
            icon="⚡"
            forceOpen={isDgcaEligibleTicket(ticket)}
          >
            {/* What will happen summary */}
            <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-3 space-y-1.5 text-sm">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                What will happen when you confirm
              </div>
              <div className="flex items-start gap-2 text-gray-700">
                <span className="text-gray-400 mt-0.5">1.</span>
                <span>Booking will be cancelled with the airline</span>
              </div>
              <div className="flex items-start gap-2 text-gray-700">
                <span className="text-gray-400 mt-0.5">2.</span>
                <span>Refund will be sent automatically to the customer (cash to bank, wallet/gift card to app balance)</span>
              </div>
              <div className="flex items-start gap-2 text-gray-700">
                <span className="text-gray-400 mt-0.5">3.</span>
                <span>Ticket will be closed and a history entry created</span>
              </div>
            </div>

            {/* Refund summary from computed breakdown */}
            {autoBreakdown?.refund_split && (() => {
              const cashAmt = Number(autoBreakdown.refund_split.cash_to_razorpay) || 0;
              const gcAmt = Number(autoBreakdown.refund_split.giftcard_to_wallet) || 0;
              const navAmt = Number(autoBreakdown.refund_split.navcash_to_wallet) || 0;
              const totalAmt = Number(autoBreakdown.refund_split.total_customer_receives) || 0;
              const isGiftCardOnly = gcAmt > 0 && cashAmt === 0 && navAmt === 0;
              return (
                <div className={`rounded-xl border px-3 py-3 space-y-1.5 ${isGiftCardOnly ? "bg-purple-50 border-purple-200" : "bg-blue-50 border-blue-200"}`}>
                  <div className={`text-xs font-bold uppercase tracking-wide mb-1 ${isGiftCardOnly ? "text-purple-700" : "text-blue-700"}`}>
                    Refund Summary (from Step 2)
                  </div>
                  {isGiftCardOnly ? (
                    <div className="rounded-lg bg-purple-100 border border-purple-300 px-2.5 py-2 text-xs text-purple-900 font-medium">
                      Gift card booking — no cash refund to bank. Full eligible amount will be credited to the customer&apos;s in-app wallet.
                    </div>
                  ) : cashAmt > 0 ? (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Cash refund to bank account</span>
                      <span className="font-semibold text-gray-900">{formatINR(cashAmt)}</span>
                    </div>
                  ) : null}
                  {navAmt > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">NavCash → wallet</span>
                      <span className="font-semibold text-blue-700">{formatINR(navAmt)}</span>
                    </div>
                  )}
                  {gcAmt > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Gift card refund → wallet</span>
                      <span className="font-semibold text-purple-700">{formatINR(gcAmt)}</span>
                    </div>
                  )}
                  {totalAmt != null && (
                    <div className={`flex justify-between text-sm border-t pt-1.5 mt-0.5 ${isGiftCardOnly ? "border-purple-200" : "border-blue-200"}`}>
                      <span className="font-semibold text-gray-700">Total customer receives</span>
                      <span className={`font-bold ${isGiftCardOnly ? "text-purple-800" : "text-blue-800"}`}>{formatINR(totalAmt)}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Cancel type confirmation */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block font-medium">
                Cancellation Type
              </label>
              <select
                value={cancelTypeLabel}
                onChange={(e) => {
                  const label = e.target.value;
                  setCancelTypeLabel(label);
                  const row = typeOptions.find((o) => o.label === label);
                  if (row) setCancelType(row.cancelType);
                }}
                disabled={cancelSubmitting}
                className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black disabled:opacity-60"
              >
                {typeOptions.map((o) => (
                  <option key={o.label} value={o.label}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Partial passenger selection summary */}
            {cancelType === 2 && (() => {
              const selected = (selectedPassengers || []).filter((id) =>
                (passengersWithIds || []).some((p) => p.passenger_id === id)
              );
              const names = selected
                .map((id) => {
                  const p = (passengersWithIds || []).find((p) => p.passenger_id === id);
                  return p
                    ? [p.title, p.first_name, p.last_name].filter(Boolean).join(" ").trim()
                    : null;
                })
                .filter(Boolean);

              if (selected.length === 0) {
                return (
                  <div className="rounded-xl bg-yellow-50 border border-yellow-300 px-3 py-2 text-xs text-yellow-800">
                    ⚠ No passengers selected. Select passengers from the table on the left before executing partial cancellation.
                  </div>
                );
              }
              return (
                <div className="rounded-xl bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800 space-y-1">
                  <div className="font-semibold">Cancelling for {selected.length} passenger{selected.length > 1 ? "s" : ""}:</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {names.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              );
            })()}

            {/* Partial passenger validation error */}
            {cancelType === 2 && partialCancelSelectionError && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                ⚠ {partialCancelSelectionError}
              </div>
            )}

            {/* Remarks */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block font-medium">
                Remarks{" "}
                <span className="font-normal text-gray-400">(optional — sent to airline)</span>
              </label>
              <textarea
                value={cancelRemarks}
                onChange={(e) => setCancelRemarks(e.target.value)}
                rows={2}
                placeholder="e.g. Customer confirmed via call on 20 May 2026"
                disabled={cancelSubmitting}
                className="w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black resize-none disabled:opacity-60"
              />
            </div>

            {awaitingCustomer && (
              <div className="rounded-xl border border-blue-300 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                {ticket?.cancellation_status === "customer_accepted"
                  ? "The customer accepted and the automatic cancellation is running or did not complete. Check the ticket history before cancelling here."
                  : "Waiting for the customer to Accept in the app. Their Accept cancels with the airline and refunds automatically, and this page updates on its own. Use this button only if the customer confirmed another way or the automatic cancellation failed."}
              </div>
            )}

            {/* Bot warning */}
            {ticket?.bot_response && !ticket?.cancelled_via_bot && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                ⚠️ This booking was submitted via bot. Verify with the airline system before
                confirming — ensure the cancellation is not already in progress.
              </div>
            )}

            {/* DGCA gate — mirrors the backend's tbo_dgca_confirmed check */}
            {chargesNeedRefresh && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 space-y-2">
                <p className="text-xs font-semibold text-amber-900">{chargesNeedRefresh}</p>
                <button
                  type="button"
                  onClick={handleGetCharges}
                  disabled={cancellationLoading}
                  className="w-full rounded-xl bg-amber-600 px-4 py-2 text-white text-sm font-bold hover:bg-amber-700 disabled:opacity-50 transition-colors"
                >
                  {cancellationLoading ? "Refreshing…" : "Refresh Airline Charges"}
                </button>
                <p className="text-[11px] text-amber-800">
                  Then click &ldquo;Cancel Booking &amp; Process Refund&rdquo; again.
                </p>
              </div>
            )}

            {dgcaBlocksExecution && (
              <DgcaConfirmForm
                tboDgcaNote={tboDgcaNote}
                setTboDgcaNote={setTboDgcaNote}
                tboDgcaReference={tboDgcaReference}
                setTboDgcaReference={setTboDgcaReference}
                tboDgcaSubmitting={tboDgcaSubmitting}
                onConfirmTboDgca={handleConfirmTboDgca}
              />
            )}

            {/* Execute button — opens confirmation dialog */}
            <button
              type="button"
              disabled={
                cancelSubmitting ||
                dgcaBlocksExecution ||
                (cancelType === 2 && partialCancelSelectionError != null) ||
                (cancelType === 2 &&
                  (selectedPassengers || []).filter((id) =>
                    (passengersWithIds || []).some((p) => p.passenger_id === id)
                  ).length === 0)
              }
              onClick={() => setShowConfirmDialog(true)}
              className="w-full rounded-xl bg-red-600 px-4 py-3 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {cancelSubmitting
                ? "Processing…"
                : dgcaBlocksExecution
                ? "Confirm TBO Refund First"
                : "Cancel Booking & Process Refund"}
            </button>
            <p className="text-[10px] text-gray-400 text-center">
              This action is irreversible. Make sure charges and refund are correct before confirming.
            </p>

            {/* ── Confirmation dialog ─────────────────────────────────── */}
            {showConfirmDialog && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">⚠️</span>
                    <h3 className="text-base font-bold text-gray-900">Confirm Cancellation</h3>
                  </div>

                  <p className="text-sm text-gray-600">
                    You are about to <strong>cancel this booking</strong> and process the refund.
                    This action <strong>cannot be undone</strong>.
                  </p>

                  {/* Summary */}
                  <div className="rounded-xl bg-red-50 border border-red-200 p-3 space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Type</span>
                      <span className="font-semibold text-gray-800">{cancelTypeLabel}</span>
                    </div>
                    {isDgcaEligibleTicket(ticket) && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">DGCA case</span>
                        <span className="font-semibold text-amber-700">
                          {Number(ticket?.tbo_dgca_confirmed) === 1 ? "TBO confirmed ✓" : "NOT confirmed with TBO"}
                        </span>
                      </div>
                    )}
                    {autoBreakdown?.refund_split?.total_customer_receives != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Refund to customer</span>
                        <span className="font-semibold text-green-700">
                          ₹{Number(autoBreakdown.refund_split.total_customer_receives).toLocaleString("en-IN")}
                        </span>
                      </div>
                    )}
                    {cancellationCharges?.charges?.CancellationCharge != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Airline cancellation charge</span>
                        <span className="font-semibold text-red-600">
                          ₹{Number(cancellationCharges.charges.CancellationCharge).toLocaleString("en-IN")}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setShowConfirmDialog(false)}
                      className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Go Back
                    </button>
                    <button
                      type="button"
                      disabled={cancelSubmitting || dgcaBlocksExecution}
                      onClick={() => {
                        setShowConfirmDialog(false);
                        handleExecute();
                      }}
                      className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {cancelSubmitting ? "Processing…" : "Yes, Cancel Booking"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </StepCard>
          )}

        </div>
      )}
    </div>
  );
}
